<template>
    <div class="card-collect">
        <!-- 登入導航 -->
        <LoginNavbar @login-success="onLoginSuccess" @logout-success="onLogoutSuccess" ref="loginNavbar" />
        
        <div class="content-wrapper">
            <div v-if="loading" class="loading">
                {{ $t('htwbcard.collect.loading') }}
            </div>
            <div v-else-if="cardCollectInfo">
                <div class="card-collect-name">
                    <!-- 左側：返回按鈕和標題 -->
                    <div class="title-left">
                        <span class="home-link" v-if="isCollectCreator" @click="goHome()">
                            <svg-icon icon-class="chevron-left" />
                        </span>
                        {{ cardCollectInfo.cardCollectName }}
                    </div>
                    
                    <!-- 中間：搜尋框 -->
                    <div class="search-box">
                        <!-- 放大鏡圖示 -->
                        <div class="search-icon">
                            <svg-icon icon-class="search" />
                        </div>
                        <input 
                            type="text" 
                            v-model="searchText" 
                            :placeholder="$t('htwbcard.collect.searchCardPlaceholder')"
                            @input="onSearchInput"
                            @keyup.enter="performSearch"
                            class="search-input"
                        />
                        <button 
                            v-if="searchText" 
                            @click="clearSearch" 
                            class="clear-search-btn"
                            :title="$t('htwbcard.clearSearch')"
                        >
                            <svg-icon icon-class="close" />
                        </button>
                        
                        <!-- 搜尋結果資訊 -->
                        <div v-if="searchText" class="search-results-count">
                            <span v-if="isSearching">{{ $t('htwbcard.searching') }}</span>
                            <span v-else-if="searchResults">{{ $t('htwbcard.collect.searchResultsCount', { count: searchResults.length }) }}</span>
                        </div>
                    </div>
                    
                    <!-- 右側：顏色篩選區域 -->
                    <div class="color-filter-container">
                        <!-- 共用按鈕 (僅創建者可見) -->
                        <button 
                            v-if="isCollectCreator"
                            class="share-btn"
                            :title="$t('htwbcard.collect.shareCard')"
                            @click="openShareModal"
                        >
                            <svg-icon icon-class="link" />
                            <span>{{ $t('htwbcard.collect.share') }}</span>
                        </button>
                        
                        <!-- 顏色篩選按鈕 -->
                        <button 
                            @click="toggleColorFilter" 
                            class="color-filter-btn"
                            :class="{ active: showColorFilter || selectedColorFilter }"
                            :title="$t('htwbcard.collect.colorFilter')"
                        >
                            <svg-icon icon-class="filter" />
                        </button>

                        <!-- 顏色篩選面板 -->
                        <div v-if="showColorFilter" class="color-filter-panel">
                            <div class="filter-header">
                                <span class="filter-title">{{ $t('htwbcard.collect.colorTitle') }}</span>
                                <button 
                                    v-if="selectedColorFilter" 
                                    @click="clearColorFilter" 
                                    class="clear-filter-btn"
                                >
                                    {{ $t('htwbcard.collect.clearFilter') }}
                                </button>
                            </div>
                            <div class="color-filter-options">
                                <div 
                                    v-for="color in colorPresets" 
                                    :key="color.value"
                                    class="color-filter-item"
                                    :class="{ active: selectedColorFilter === color.value }"
                                    @click="selectColorFilter(color.value)"
                                >
                                    <div 
                                        class="color-dot"
                                        :style="{ backgroundColor: color.value }"
                                    ></div>
                                    <span class="color-name">{{ color.name.replace('(預設)', '') }}</span>
                                </div>
                            </div>
                        </div>
                    </div>
                    
                    <!-- 圓形上傳按鈕 -->
                    <button 
                        v-if="cardCollectInfo && cardCollectInfo.openSubmit" 
                        class="floating-upload-btn" 
                        @click="showUploadModal = true"
                        :title="$t('htwbcard.collect.uploadCard')"
                    >
                        <svg-icon icon-class="pen" />
                    </button>
                </div>

                <!-- Padlet 風格的分組欄位布局 -->
                <div v-if="cardCollectInfo && cardCollectInfo.groups" class="padlet-columns">
                    <div 
                        v-for="group in cardCollectInfo.groups" 
                        :key="group.groupId"
                        class="column"
                    >
                        <!-- 分組標題按鈕 -->
                        <div class="group-header">
                            <button 
                                :class="['group-title-btn', { active: selectedGroupId === group.groupId }]"
                                @click="selectedGroupId = group.groupId"
                            >
                                {{ group.groupName }}
                            </button>
                        </div>
                        
                        <!-- 該分組的卡片 -->
                        <div 
                            class="column-cards"
                            @drop="handleCardDrop($event, group.groupId)"
                            @dragover="handleCardDragOver"
                            @dragenter="handleCardDragEnter"
                            @dragleave="handleCardDragLeave"
                            :class="{ 'drag-over': isDragOverGroup === group.groupId }"
                        >
                            <div 
                                v-for="card in getDisplayCards(group.groupId)" 
                                :key="card.id" 
                                class="card-item"
                                :class="{ 'my-card': isMyCard(card) }"
                                :style="{ backgroundColor: card.cardColor || '#FFFFFF' }"
                                draggable="true"
                                @dragstart="handleCardDragStart($event, card)"
                                @dragend="handleCardDragEnd"
                            >
                                <div class="card-header">
                                    <h4>{{ card.cardName }}</h4>
                                </div>
                                
                                <!-- 顯示文字內容 -->
                                <div v-if="card.textContent" class="card-text-content">
                                    <p class="text-preview">{{ card.textContent }}</p>
                                </div>
                                
                                <!-- 顯示檔案資訊 -->
                                <div v-if="card.filePath" class="card-file-info">
                                    <!-- 圖片預覽 -->
                                    <div 
                                        v-if="isImageFileType(card.fileType)" 
                                        class="card-image-preview"
                                        @click="openImagePreview(card)"
                                    >
                                        <div class="image-container">
                                            <img 
                                                :src="getCardFileUrl(card)"
                                                :alt="card.cardName"
                                                @load="onImageLoad(card.id)"
                                                @error="onImageError(card.id)"
                                                class="preview-image"
                                                :style="{ display: imageErrorMap[card.id] ? 'none' : 'block' }"
                                            />
                                            <div 
                                                v-show="imageErrorMap[card.id]"
                                                class="image-error"
                                            >
                                                <svg-icon icon-class="add_img" />
                                                {{ $t('htwbcard.collect.imageLoadFailed') }}
                                            </div>
                                        </div>
                                    </div>
                                    
                                    <!-- 影片播放器 -->
                                    <div v-else-if="isVideoFileType(card.fileType)" class="card-video-preview">
                                        <video 
                                            :src="getCardFileUrl(card)"
                                            controls
                                            preload="metadata"
                                            class="preview-video"
                                            @error="onVideoError(card.id)"
                                        >
                                            {{ $t('htwbcard.collect.videoNotSupported') }}
                                        </video>
                                    </div>
                                    
                                    <!-- 音訊播放器 -->
                                    <div v-else-if="isAudioFileType(card.fileType)" class="card-audio-preview">
                                        <div class="audio-info">
                                            <svg-icon icon-class="music-note" />
                                            <span class="audio-name">{{ card.cardName }}</span>
                                        </div>
                                        <audio 
                                            :src="getCardFileUrl(card)"
                                            controls
                                            preload="metadata"
                                            class="preview-audio"
                                            @error="onAudioError(card.id)"
                                        >
                                            {{ $t('htwbcard.collect.audioNotSupported') }}
                                        </audio>
                                    </div>
                                    
                                    <!-- 其他檔案類型 - 顯示檔名連結 -->
                                    <div v-else class="card-file-download">
                                        <a 
                                            href="#"
                                            @click.prevent="downloadFile(card)"
                                            class="file-name-link"
                                        >
                                            {{ getOriginalFileName(card) }}
                                        </a>
                                    </div>
                                </div>
                                
                                <div class="card-footer">
                                    <p class="card-owner">{{ card.owner?.name }}</p>
                                    <p class="card-update-time">{{ formatTime(card.updateTime) }}</p>
                                    <!-- 只有能編輯的卡片才顯示選單 -->
                                    <div class="card-menu" v-if="canEditCard(card)">
                                        <button class="menu-btn" @click="openCardMenu(card, $event)">
                                            <svg-icon icon-class="more" />
                                        </button>
                                    </div>
                                </div>

                                <!-- 留言區域 -->
                                <div class="card-comments-section">
                                    <!-- 留言輸入框 -->
                                    <div class="comment-input-container">
                                        <!-- 隱藏的檔案輸入框 -->
                                        <input 
                                            :id="`fileInput-${card.id}`"
                                            type="file" 
                                            style="display: none"
                                            multiple
                                            @change="handleCommentFileSelect(card.id, $event)"
                                        />
                                        
                                        <input 
                                            v-model="commentInputs[card.id]"
                                            type="text" 
                                            :placeholder="$t('htwbcard.collect.comments.placeholder')"
                                            class="comment-input"
                                            :class="{ 
                                                'comment-input-focused': commentInputFocused[card.id],
                                                'comment-input-has-content': commentInputs[card.id] && commentInputs[card.id].trim()
                                            }"
                                            @keyup.enter="submitComment(card.id)"
                                            @focus="onCommentInputFocus(card.id)"
                                            @blur="onCommentInputBlur(card.id)"
                                        />
                                        
                                        <!-- 檔案上傳按鈕 -->
                                        <button 
                                            @click="triggerFileUpload(card.id)"
                                            class="comment-file-btn"
                                            title="上傳檔案"
                                        >
                                            <svg-icon icon-class="upload" />
                                        </button>
                                        
                                        <button 
                                            v-if="commentInputs[card.id] && commentInputs[card.id].trim() || commentAttachments[card.id]?.length > 0"
                                            @click="submitComment(card.id)"
                                            class="comment-submit-btn"
                                            :disabled="isUploadingFiles[card.id] || isSubmittingComment[card.id]"
                                        >
                                            <svg-icon icon-class="arrowright" />
                                        </button>
                                    </div>
                                    
                                    <!-- 檔案預覽區域 -->
                                    <div v-if="commentAttachments[card.id]?.length > 0" class="comment-attachments-preview">
                                        <div 
                                            v-for="(file, index) in commentAttachments[card.id]" 
                                            :key="index"
                                            class="attachment-preview-item"
                                        >
                                            <!-- 圖片預覽 -->
                                            <div v-if="file.type?.startsWith('image/')" class="attachment-image-preview">
                                                <img :src="file.preview" :alt="file.name" class="preview-image" />
                                            </div>
                                            
                                            <!-- 其他檔案類型預覽 -->
                                            <div v-else class="attachment-file-preview">
                                                <svg-icon 
                                                    :icon-class="getFileIcon(file.type)" 
                                                    class="file-type-icon"
                                                />
                                                <span class="file-name">{{ file.name }}</span>
                                            </div>
                                            
                                            <!-- 上傳進度 -->
                                            <div v-if="file.uploading" class="upload-progress">
                                                <div class="progress-bar">
                                                    <div 
                                                        class="progress-fill" 
                                                        :style="{ width: `${file.progress || 0}%` }"
                                                    ></div>
                                                </div>
                                                <span class="progress-text">{{ file.progress || 0 }}%</span>
                                            </div>
                                            
                                            <!-- 刪除按鈕 -->
                                            <button 
                                                @click="removeAttachment(card.id, index)"
                                                class="remove-attachment-btn"
                                                :disabled="file.uploading"
                                            >
                                                <svg-icon icon-class="close" />
                                            </button>
                                        </div>
                                    </div>

                                    <!-- 留言列表 -->
                                    <div class="comments-list" v-if="getCardComments(card.id).length > 0">
                                        <div 
                                            v-for="comment in getCardComments(card.id)" 
                                            :key="comment.id"
                                            class="comment-item"
                                            :class="{ 'comment-editing': comment.isEditing }"
                                        >
                                            <div class="comment-header">
                                                <!-- 頭像：如果有圖片URL則顯示圖片，否則顯示SVG圖標 -->
                                                <img 
                                                    v-if="comment.commenter.picture && comment.commenter.picture.trim()"
                                                    :src="comment.commenter.picture" 
                                                    :alt="comment.commenter.name"
                                                    class="comment-avatar"
                                                />
                                                <div 
                                                    v-else
                                                    class="comment-avatar comment-avatar-default"
                                                >
                                                    <svg-icon icon-class="profile" />
                                                </div>
                                                <div class="comment-info">
                                                    <span class="comment-author">{{ comment.commenter.name }}</span>
                                                    <span class="comment-time">{{ formatCommentTime(comment.time) }}</span>
                                                </div>
                                                <!-- 編輯/刪除選單 -->
                                                <div class="comment-actions" v-if="canEditComment(comment)">
                                                    <button 
                                                        class="comment-action-btn"
                                                        @click="toggleCommentMenu(comment.id)"
                                                    >
                                                        <svg-icon icon-class="more" />
                                                    </button>
                                                    <div 
                                                        v-if="activeCommentMenu === comment.id"
                                                        class="comment-menu"
                                                    >
                                                        <button @click="editComment(comment)">{{ $t('htwbcard.collect.comments.edit') }}</button>
                                                        <button @click="deleteComment(comment.id)">{{ $t('htwbcard.collect.comments.delete') }}</button>
                                                    </div>
                                                </div>
                                            </div>
                                            <div class="comment-content">
                                                <p v-if="!comment.isEditing && comment.content">{{ comment.content }}</p>
                                                
                                                <!-- 顯示附件 -->
                                                <div v-if="!comment.isEditing && comment.attachments && comment.attachments.length > 0" class="comment-attachments">
                                                    <div 
                                                        v-for="attachment in comment.attachments" 
                                                        :key="attachment.id"
                                                        class="comment-attachment-item"
                                                    >
                                                        <!-- 圖片附件 -->
                                                        <div v-if="isImageFileByPath(attachment.filePath)" class="attachment-image">
                                                            <img 
                                                                :src="getAttachmentUrl(attachment.filePath)"
                                                                :alt="getFileNameFromPath(attachment.filePath)"
                                                                class="attachment-image-display"
                                                                @click="openAttachmentImagePreview(attachment.filePath, getFileNameFromPath(attachment.filePath))"
                                                            />
                                                        </div>
                                                        
                                                        <!-- 影片附件 -->
                                                        <div v-else-if="isVideoFileByPath(attachment.filePath)" class="attachment-video">
                                                            <video 
                                                                :src="getAttachmentUrl(attachment.filePath)"
                                                                class="attachment-video-display"
                                                                controls
                                                                preload="metadata"
                                                                @click.stop
                                                            >
                                                                您的瀏覽器不支援影片播放
                                                            </video>
                                                        </div>
                                                        
                                                        <!-- 音頻附件 (套用主卡片樣式) -->
                                                        <div v-else-if="isAudioFileByPath(attachment.filePath)" class="card-audio-preview">
                                                            <div class="audio-info">
                                                                <svg-icon icon-class="music-note" />
                                                                <span class="audio-name">{{ getFileNameFromPath(attachment.filePath) }}</span>
                                                            </div>
                                                            <audio 
                                                                :src="getAttachmentUrl(attachment.filePath)"
                                                                controls
                                                                preload="metadata"
                                                                class="preview-audio"
                                                            >
                                                                您的瀏覽器不支援音頻播放
                                                            </audio>
                                                        </div>
                                                        
                                                        <!-- 其他檔案類型 -->
                                                        <div v-else class="attachment-file">
                                                            <a 
                                                                :href="getAttachmentUrl(attachment.filePath)"
                                                                target="_blank"
                                                                class="attachment-file-link"
                                                            >
                                                                <svg-icon 
                                                                    icon-class="doc" 
                                                                    class="attachment-file-icon"
                                                                />
                                                                <span class="attachment-file-name">{{ getFileNameFromPath(attachment.filePath) }}</span>
                                                            </a>
                                                        </div>
                                                    </div>
                                                </div>
                                                
                                                <div v-if="comment.isEditing" class="comment-edit-container">
                                                    <input 
                                                        v-model="comment.editContent"
                                                        type="text"
                                                        class="comment-edit-input"
                                                        @keyup.enter="saveCommentEdit(comment)"
                                                        @keyup.esc="cancelCommentEdit(comment)"
                                                    />
                                                    
                                                    <!-- 編輯模式下的附件管理 -->
                                                    <div v-if="comment.editAttachments && comment.editAttachments.length > 0" class="comment-edit-attachments">
                                                        <div 
                                                            v-for="(attachment, index) in comment.editAttachments" 
                                                            :key="attachment.id || index"
                                                            class="edit-attachment-item"
                                                        >
                                                            <!-- 圖片附件預覽 -->
                                                            <div v-if="isImageFileByPath(attachment.filePath)" class="edit-attachment-preview">
                                                                <img 
                                                                    :src="getAttachmentUrl(attachment.filePath)"
                                                                    :alt="getFileNameFromPath(attachment.filePath)"
                                                                    class="edit-attachment-image"
                                                                />
                                                            </div>
                                                            
                                                            <!-- 影片附件預覽 -->
                                                            <div v-else-if="isVideoFileByPath(attachment.filePath)" class="edit-attachment-preview">
                                                                <video 
                                                                    :src="getAttachmentUrl(attachment.filePath)"
                                                                    class="edit-attachment-video"
                                                                    preload="metadata"
                                                                >
                                                                </video>
                                                            </div>
                                                            
                                                            <!-- 其他檔案類型 -->
                                                            <div v-else class="edit-attachment-file">
                                                                <div class="edit-file-icon">
                                                                    <svg-icon :icon-class="getFileIconByPath(attachment.filePath)" />
                                                                </div>
                                                                <span class="edit-file-name">{{ getFileNameFromPath(attachment.filePath) }}</span>
                                                            </div>
                                                            
                                                            <!-- 刪除附件按鈕 -->
                                                            <button 
                                                                @click="removeEditAttachment(comment, index)"
                                                                class="remove-edit-attachment-btn"
                                                                :title="$t('htwbcard.collect.comments.removeAttachment')"
                                                            >
                                                                <svg-icon icon-class="close" />
                                                            </button>
                                                        </div>
                                                    </div>
                                                    
                                                    <!-- 編輯操作按鈕區（水平並列） -->
                                                    <div class="comment-edit-bottom-actions">
                                                        <!-- 更換附件按鈕 -->
                                                        <div class="comment-edit-file-actions">
                                                            <input 
                                                                type="file" 
                                                                @change="handleEditCommentFileSelect($event, comment)"
                                                                class="hidden-file-input"
                                                                :ref="'editFileInput_' + comment.id"
                                                            />
                                                            <button 
                                                                @click="$refs['editFileInput_' + comment.id][0].click()"
                                                                class="icon-action-btn upload-btn"
                                                                :title="$t('htwbcard.collect.comments.addAttachment')"
                                                            >
                                                                <svg-icon icon-class="upload" />
                                                                <span>{{ $t('htwbcard.collect.comments.replaceAttachment') }}</span>
                                                            </button>
                                                        </div>
                                                        
                                                        <!-- 儲存和取消按鈕 -->
                                                        <div class="comment-edit-actions">
                                                            <button @click="saveCommentEdit(comment)" class="icon-action-btn save-btn" :title="$t('htwbcard.collect.comments.save')">
                                                                <svg-icon icon-class="check" />
                                                            </button>
                                                            <button @click="cancelCommentEdit(comment)" class="icon-action-btn cancel-btn" :title="$t('htwbcard.collect.comments.cancel')">
                                                                <svg-icon icon-class="close" />
                                                            </button>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                    
                    <!-- 新增分組欄位 -->
                    <div class="column add-column">
                        <div class="group-header">
                            <button class="add-group-btn" @click="showAddGroupModal = true" :title="$t('htwbcard.collect.addGroup')">
                                +
                            </button>
                        </div>
                    </div>
                </div>
            </div>
            <div v-else class="error">
                {{ $t('htwbcard.collect.loadFailed') }}
            </div>
        </div>

        <!-- 上傳卡片 Modal -->
        <div v-show="showUploadModal" class="modal-overlay">
            <div class="modal-content upload-modal">
                <div class="modal-header fixed-header">
                    <h3>{{ $t('htwbcard.collect.uploadModal.title') }}</h3>
                    <button class="close-btn" @click="closeUploadModal">
                        <svg-icon icon-class="close" />
                    </button>
                </div>

                <div class="modal-body scrollable-content">
                    <div class="form-group">
                        <label for="cardName">{{ $t('htwbcard.collect.uploadModal.cardName') }}</label>
                        <input 
                            id="cardName"
                            type="text" 
                            v-model="uploadForm.cardName" 
                            :placeholder="$t('htwbcard.collect.uploadModal.cardNamePlaceholder')"
                            maxlength="50"
                            required
                            :class="{ 'error': uploadForm.errors.cardName }"
                        />
                        <span v-if="uploadForm.errors.cardName" class="error-message">{{ uploadForm.errors.cardName }}</span>
                    </div>
                    

                    <!-- 分組選擇 -->
                    <div v-if="cardCollectInfo && cardCollectInfo.groups" class="form-group">
                        <label for="groupSelect">{{ $t('htwbcard.collect.uploadModal.selectGroup') }}</label>
                        <select 
                            id="groupSelect"
                            v-model="uploadForm.groupId"
                            class="group-select"
                        >
                            <option 
                                v-for="group in cardCollectInfo.groups" 
                                :key="group.groupId" 
                                :value="group.groupId"
                            >
                                {{ group.groupName }}
                            </option>
                        </select>
                    </div>

                    <!-- 文字內容輸入區域 -->
                    <div class="form-group">
                        <label for="textContent">{{ $t('htwbcard.collect.uploadModal.textContent') }}</label>
                        <textarea 
                            id="textContent"
                            v-model="uploadForm.textContent" 
                            :placeholder="$t('htwbcard.collect.uploadModal.textContentPlaceholder')"
                            rows="4"
                            maxlength="1000"
                            :class="{ 'error': uploadForm.errors.textContent }"
                        ></textarea>
                        <div class="char-counter">{{ uploadForm.textContent.length }}/1000</div>
                        <span v-if="uploadForm.errors.textContent" class="error-message">{{ uploadForm.errors.textContent }}</span>
                    </div>

                    <!-- 卡片顏色選擇 -->
                    <div class="form-group">
                        <label for="cardColor">{{ $t('htwbcard.collect.uploadModal.cardColor') }}</label>
                        <div class="color-picker-container">
                            <div class="color-presets">
                                <div 
                                    v-for="color in colorPresets" 
                                    :key="color.name"
                                    class="color-preset"
                                    :class="{ active: uploadForm.cardColor === color.value }"
                                    :style="{ backgroundColor: color.value }"
                                    @click="uploadForm.cardColor = color.value"
                                    :title="color.name"
                                ></div>
                            </div>
                        </div>
                    </div>
                    
                    <!-- 檔案上傳區域 -->
                    <div class="form-group">
                        <label for="fileInput">{{ $t('htwbcard.collect.uploadModal.selectFile') }}</label>
                        <div 
                            class="file-upload-area"
                            :class="{ 'drag-over': isDragOver, 'error': uploadForm.errors.file }"
                            @drop="handleFileDrop"
                            @dragover="handleFileDragOver"
                            @dragenter="handleFileDragEnter"
                            @dragleave="handleFileDragLeave"
                        >
                            <input 
                                id="fileInput"
                                type="file" 
                                ref="fileInput"
                                @change="handleFileSelect"
                                accept="*/*"
                                style="display: none;"
                            />
                            <div v-if="!uploadForm.selectedFile" class="file-placeholder" @click="fileInput?.click()">
                                <div class="file-placeholder-icon">
                                    <svg-icon icon-class="file-left" />
                                </div>
                                <p>{{ $t('htwbcard.collect.uploadModal.dragDropFile') }}</p>
                                <p class="file-hint">{{ $t('htwbcard.collect.uploadModal.fileHint') }}</p>
                                <p class="file-size-hint">{{ $t('htwbcard.collect.uploadModal.fileSizeHint', { size: maxFileSize }) }}</p>
                            </div>
                            <div v-else class="file-selected">
                                <!-- 圖片預覽 -->
                                <div v-if="isImageFile(uploadForm.selectedFile)" class="file-preview-container">
                                    <div class="file-preview-image" @click="previewUploadImage">
                                        <img 
                                            :src="getFilePreviewUrl(uploadForm.selectedFile)"
                                            :alt="uploadForm.selectedFile.name"
                                            class="preview-thumbnail"
                                        />
                                    </div>
                                    <div class="file-info">
                                        <span class="file-name">{{ uploadForm.selectedFile.name }}</span>
                                        <span class="file-size">{{ formatFileSize(uploadForm.selectedFile.size) }}</span>
                                        <span class="file-type">{{ getDisplayFileType(getFileType(uploadForm.selectedFile)) }}</span>
                                    </div>
                                    <button type="button" class="remove-file-btn" @click="removeFile" :title="$t('htwbcard.collect.uploadModal.removeFile')">
                                        <svg-icon icon-class="close" />
                                    </button>
                                </div>
                                
                                <!-- 非圖片檔案 -->
                                <div v-else class="file-info-container">
                                    <div class="file-icon">
                                        <svg-icon v-if="isVideoFile(uploadForm.selectedFile)" icon-class="video-camera" />
                                        <svg-icon v-else icon-class="doc" />
                                    </div>
                                    <div class="file-info">
                                        <span class="file-name">{{ uploadForm.selectedFile.name }}</span>
                                        <span class="file-size">{{ formatFileSize(uploadForm.selectedFile.size) }}</span>
                                        <span class="file-type">{{ getDisplayFileType(getFileType(uploadForm.selectedFile)) }}</span>
                                    </div>
                                    <button type="button" class="remove-file-btn" @click="removeFile" :title="$t('htwbcard.collect.uploadModal.removeFile')">
                                        <svg-icon icon-class="close" />
                                    </button>
                                </div>
                            </div>
                        </div>
                        <span v-if="uploadForm.errors.file" class="error-message">{{ uploadForm.errors.file }}</span>
                    </div>

                    <!-- 上傳進度 -->
                    <div v-if="uploading" class="upload-progress">
                        <div class="progress-info">
                            <span class="progress-label">{{ uploadStatus }}</span>
                            <span class="progress-text">{{ uploadProgress }}%</span>
                        </div>
                        <div class="progress-bar">
                            <div class="progress-fill" :style="{ width: uploadProgress + '%' }"></div>
                        </div>
                    </div>

                    <!-- 錯誤訊息 -->
                    <div v-if="uploadForm.generalError" class="general-error">
                        <svg-icon icon-class="close" />
                        <span>{{ uploadForm.generalError }}</span>
                    </div>
                </div>

                <div class="modal-footer fixed-footer">
                    <button type="button" class="cancel-btn" @click="closeUploadModal" :disabled="uploading">
                        {{ $t('htwbcard.collect.uploadModal.cancel') }}
                    </button>
                    <button 
                        type="button" 
                        class="submit-btn" 
                        @click="uploadCard" 
                        :disabled="!canSubmit || uploading"
                    >
                        {{ uploading ? $t('htwbcard.collect.uploadModal.uploading') : $t('htwbcard.collect.uploadModal.uploadCard') }}
                    </button>
                </div>
            </div>
        </div>

        <!-- 卡片操作選單 -->
        <div v-show="showCardMenu" class="card-actions" :style="{ left: cardMenuPos.x + 'px', top: cardMenuPos.y + 'px' }">
            <div class="action-item edit-action" @click="openEditModal">
                <svg-icon icon-class="edit" />
                {{ $t('htwbcard.collect.cardMenu.edit') }}
            </div>
            <div class="action-item delete-action" @click="openDeleteConfirm">
                <svg-icon icon-class="delete" />
                {{ $t('htwbcard.collect.cardMenu.delete') }}
            </div>
        </div>

        <!-- 編輯卡片 Modal -->
        <div v-show="showEditModal" class="modal-overlay">
            <div class="modal-content edit-modal">
                <div class="modal-header fixed-header">
                    <h3>{{ $t('htwbcard.collect.editModal.title') }}</h3>
                    <button class="close-btn" @click="closeEditModal">
                        <svg-icon icon-class="close" />
                    </button>
                </div>

                <div class="modal-body scrollable-content">
                    <div class="form-group">
                        <label for="editCardName">{{ $t('htwbcard.collect.uploadModal.cardName') }}</label>
                        <input 
                            id="editCardName"
                            type="text" 
                            v-model="editForm.cardName" 
                            :placeholder="$t('htwbcard.collect.uploadModal.cardNamePlaceholder')"
                            maxlength="50"
                            required
                            :class="{ 'error': editForm.errors.cardName }"
                        />
                        <span v-if="editForm.errors.cardName" class="error-message">{{ editForm.errors.cardName }}</span>
                    </div>
                    

                    <!-- 分組選擇 -->
                    <div v-if="cardCollectInfo && cardCollectInfo.groups" class="form-group">
                        <label for="editGroupSelect">{{ $t('htwbcard.collect.uploadModal.selectGroup') }}</label>
                        <select 
                            id="editGroupSelect"
                            v-model="editForm.groupId"
                            class="group-select"
                        >
                            <option 
                                v-for="group in cardCollectInfo.groups" 
                                :key="group.groupId" 
                                :value="group.groupId"
                            >
                                {{ group.groupName }}
                            </option>
                        </select>
                    </div>

                    <!-- 文字內容輸入區域 -->
                    <div class="form-group">
                        <label for="editTextContent">{{ $t('htwbcard.collect.uploadModal.textContent') }}</label>
                        <textarea 
                            id="editTextContent"
                            v-model="editForm.textContent" 
                            :placeholder="$t('htwbcard.collect.uploadModal.textContentPlaceholder')"
                            rows="4"
                            maxlength="1000"
                            :class="{ 'error': editForm.errors.textContent }"
                        ></textarea>
                        <div class="char-counter">{{ editForm.textContent.length }}/1000</div>
                        <span v-if="editForm.errors.textContent" class="error-message">{{ editForm.errors.textContent }}</span>
                    </div>

                    <!-- 卡片顏色選擇 -->
                    <div class="form-group">
                        <label for="editCardColor">{{ $t('htwbcard.collect.uploadModal.cardColor') }}</label>
                        <div class="color-picker-container">
                            <div class="color-presets">
                                <div 
                                    v-for="color in colorPresets" 
                                    :key="color.name"
                                    class="color-preset"
                                    :class="{ active: editForm.cardColor === color.value }"
                                    :style="{ backgroundColor: color.value }"
                                    @click="editForm.cardColor = color.value"
                                    :title="color.name"
                                ></div>
                            </div>
                        </div>
                    </div>
                    
                    <!-- 檔案替換區域 -->
                    <div class="form-group">
                        <label for="editFileInput">{{ $t('htwbcard.collect.editModal.replaceFile') }}</label>
                        
                        <!-- 顯示目前檔案 -->
                        <div v-if="editForm.originalFile && !editForm.newFile" class="current-file-info">
                            <div class="file-info-header">
                                <span class="current-file-label">{{ $t('htwbcard.collect.editModal.currentFile') }}</span>
                                <div class="file-action-buttons">
                                    <button type="button" class="replace-file-btn" @click="$refs.editFileInput?.click()">
                                        {{ $t('htwbcard.collect.editModal.replaceFile') }}
                                    </button>
                                    <button type="button" class="remove-original-file-btn" @click="removeOriginalFile">
                                        <svg-icon icon-class="delete" />
                                        {{ $t('htwbcard.collect.editModal.removeCurrentFile') }}
                                    </button>
                                </div>
                            </div>
                            
                            <!-- 圖片預覽 -->
                            <div v-if="isImageFileType(editForm.originalFile.fileType)" class="current-image-preview">
                                <img 
                                    :src="getCardFileUrl(editForm.originalFile)"
                                    :alt="editForm.originalFile.cardName"
                                    class="current-preview-image"
                                    @error="onImageError(editForm.originalFile.id)"
                                />
                            </div>
                            
                            <!-- 其他檔案類型 -->
                            <div v-else class="current-file-display">
                                <div class="file-icon">
                                    <svg-icon v-if="isVideoFileType(editForm.originalFile.fileType)" icon-class="video-camera" />
                                    <svg-icon v-else icon-class="doc" />
                                </div>
                                <div class="file-info">
                                    <span class="file-name">{{ getOriginalFileName(editForm.originalFile) }}</span>
                                    <span class="file-type">{{ getDisplayFileType(editForm.originalFile.fileType) }}</span>
                                </div>
                            </div>
                        </div>
                        
                        <!-- 檔案上傳區域 -->
                        <div 
                            class="file-upload-area"
                            :class="{ 'drag-over': isEditDragOver, 'error': editForm.errors.file }"
                            @drop="handleEditFileDrop"
                            @dragover="handleEditFileDragOver"
                            @dragenter="handleEditFileDragEnter"
                            @dragleave="handleEditFileDragLeave"
                            v-show="!editForm.originalFile || editForm.newFile"
                        >
                            <input 
                                id="editFileInput"
                                type="file" 
                                ref="editFileInput"
                                @change="handleEditFileSelect"
                                accept="*/*"
                                style="display: none;"
                            />
                            <div v-if="!editForm.newFile" class="file-placeholder" @click="$refs.editFileInput?.click()">
                                <div class="file-placeholder-icon">
                                    <svg-icon icon-class="file-left" />
                                </div>
                                <p>{{ $t('htwbcard.collect.uploadModal.dragDropFile') }}</p>
                                <p class="file-hint">{{ $t('htwbcard.collect.uploadModal.fileHint') }}</p>
                                <p class="file-size-hint">{{ $t('htwbcard.collect.uploadModal.fileSizeHint', { size: maxFileSize }) }}</p>
                            </div>
                            <div v-else class="file-selected">
                                <!-- 新選擇的檔案預覽 (和上傳表單相同) -->
                                <div v-if="isImageFile(editForm.newFile)" class="file-preview-container">
                                    <div class="file-preview-image" @click="previewEditImage">
                                        <img 
                                            :src="getFilePreviewUrl(editForm.newFile)"
                                            :alt="editForm.newFile.name"
                                            class="preview-thumbnail"
                                        />
                                    </div>
                                    <div class="file-info">
                                        <span class="file-name">{{ editForm.newFile.name }}</span>
                                        <span class="file-size">{{ formatFileSize(editForm.newFile.size) }}</span>
                                        <span class="file-type">{{ getDisplayFileType(getFileType(editForm.newFile)) }}</span>
                                    </div>
                                    <button type="button" class="remove-file-btn" @click="removeEditFile" title="移除檔案">
                                        <svg-icon icon-class="close" />
                                    </button>
                                </div>
                                
                                <div v-else class="file-info-container">
                                    <div class="file-icon">
                                        <svg-icon v-if="isVideoFile(editForm.newFile)" icon-class="video-camera" />
                                        <svg-icon v-else icon-class="doc" />
                                    </div>
                                    <div class="file-info">
                                        <span class="file-name">{{ editForm.newFile.name }}</span>
                                        <span class="file-size">{{ formatFileSize(editForm.newFile.size) }}</span>
                                        <span class="file-type">{{ getDisplayFileType(getFileType(editForm.newFile)) }}</span>
                                    </div>
                                    <button type="button" class="remove-file-btn" @click="removeEditFile" title="移除檔案">
                                        <svg-icon icon-class="close" />
                                    </button>
                                </div>
                            </div>
                        </div>
                        <span v-if="editForm.errors.file" class="error-message">{{ editForm.errors.file }}</span>
                        
                    </div>

                    <!-- 錯誤訊息 -->
                    <div v-if="editForm.generalError" class="general-error">
                        <svg-icon icon-class="close" />
                        <span>{{ editForm.generalError }}</span>
                    </div>
                </div>

                <div class="modal-footer fixed-footer">
                    <button type="button" class="cancel-btn" @click="closeEditModal" :disabled="isEditing">
                        {{ $t('htwbcard.collect.uploadModal.cancel') }}
                    </button>
                    <button 
                        type="button" 
                        class="submit-btn" 
                        @click="updateCard" 
                        :disabled="!canSubmitEdit || isEditing"
                    >
                        {{ isEditing ? $t('htwbcard.collect.editModal.updating') : $t('htwbcard.collect.editModal.confirmUpdate') }}
                    </button>
                </div>
            </div>
        </div>

        <!-- 刪除確認視窗 -->
        <div v-show="showDeleteConfirm" class="modal-overlay">
            <div class="modal-content delete-confirm-modal">
                <div class="modal-header">
                    <h3>{{ $t('htwbcard.collect.deleteCardConfirm.title') }}</h3>
                    <button class="close-btn" @click="closeDeleteConfirm">
                        <svg-icon icon-class="close" />
                    </button>
                </div>
                <div class="modal-body">
                    <p class="confirm-text">{{ $t('htwbcard.collect.deleteCardConfirm.message', { name: cardToDelete?.cardName }) }}</p>
                    <p class="warning-text">{{ $t('htwbcard.collect.deleteCardConfirm.warning') }}</p>
                </div>
                <div class="modal-footer">
                    <button type="button" class="cancel-btn" @click="closeDeleteConfirm" :disabled="isDeleting">
                        {{ $t('htwbcard.collect.uploadModal.cancel') }}
                    </button>
                    <button type="button" class="delete-btn" @click="confirmDelete" :disabled="isDeleting">
                        <span v-if="isDeleting" class="loading-spinner"></span>
                        {{ isDeleting ? $t('htwbcard.collect.deleteCardConfirm.deleting') : $t('htwbcard.collect.deleteCardConfirm.confirmDelete') }}
                    </button>
                </div>
            </div>
        </div>

        <!-- 圖片預覽 Modal -->
        <div v-show="showImagePreview" class="modal-overlay image-preview-overlay" @click="closeImagePreview">
            <div class="image-preview-modal" @click.stop>
                <div class="image-preview-header">
                    <h3>{{ previewCard?.cardName }}</h3>
                    <button class="close-btn" @click="closeImagePreview">
                        <svg-icon icon-class="close" />
                    </button>
                </div>
                <div class="image-preview-content">
                    <img 
                        v-if="previewCard"
                        :src="getCardImageUrl(previewCard)"
                        :alt="previewCard.cardName"
                        class="preview-full-image"
                    />
                </div>
              
            </div>
        </div>

        <!-- 新增分組 Modal -->
        <div v-show="showAddGroupModal" class="modal-overlay">
            <div class="modal-content add-group-modal">
                <div class="modal-header">
                    <h3>{{ $t('htwbcard.collect.addGroupModal.title') }}</h3>
                    <button class="close-btn" @click="closeAddGroupModal">
                        <svg-icon icon-class="close" />
                    </button>
                </div>
                <div class="modal-body">
                    <div class="form-group">
                        <label for="newGroupName">{{ $t('htwbcard.collect.addGroupModal.groupName') }}</label>
                        <input 
                            id="newGroupName"
                            type="text" 
                            v-model="newGroupForm.groupName" 
                            :placeholder="$t('htwbcard.collect.addGroupModal.groupNamePlaceholder')"
                            maxlength="20"
                            required
                            :class="{ 'error': newGroupForm.errors.groupName }"
                        />
                        <span v-if="newGroupForm.errors.groupName" class="error-message">{{ newGroupForm.errors.groupName }}</span>
                    </div>
                    
                    <div v-if="newGroupForm.generalError" class="general-error">
                        <svg-icon icon-class="close" />
                        <span>{{ newGroupForm.generalError }}</span>
                    </div>
                </div>
                <div class="modal-footer">
                    <button type="button" class="cancel-btn" @click="closeAddGroupModal" :disabled="isAddingGroup">
                        {{ $t('htwbcard.collect.uploadModal.cancel') }}
                    </button>
                    <button 
                        type="button" 
                        class="submit-btn" 
                        @click="addNewGroup" 
                        :disabled="!newGroupForm.groupName.trim() || isAddingGroup"
                    >
                        {{ isAddingGroup ? $t('htwbcard.collect.addGroupModal.adding') : $t('htwbcard.collect.addGroupModal.confirmAdd') }}
                    </button>
                </div>
            </div>
        </div>
        
        <!-- 分享設定 Modal -->
        <div v-show="showShareModal" class="shareCard-wrap">
            <div class="shareCard">
                <div class="close-btn">
                    <svg-icon icon-class="close" @click="showShareModal = false" />
                </div>

                <div class="shareCard-title">更新收集板設定</div>

                <div class="shareCard-content">
                    <div class="rename-wrap">
                        <label class="rename-bar">
                            <input :placeholder="$t('htwbcard.forms.collectBoardName')" v-model="shareCardCollectName" maxlength="20" />
                            <div class="delete-btn" v-show="shareCardCollectName.length !== 0"
                                @click="shareCardCollectName = ''">
                                <svg-icon icon-class="close" />
                            </div>
                        </label>
                    </div>
                </div>

                <!-- 設定開關 -->
                <div class="flex-container">
                    <div class="flex-item">開放提交</div>
                    <label class="switch flex-item">
                        <input type="checkbox" v-model="shareOpenSubmit">
                        <span class="slider round"></span>
                    </label>
                </div>

                <!-- URL 顯示 -->
                <div class="url-wrap url-wrap-small" v-if="currentShareURL">
                    <span class="url-title">URL：</span>{{ shortURLHost }}<span class="short-url">{{ currentShareURL }}</span>
                    <span class="copy-btn" @click="copyShareUrl()">
                        <svg-icon icon-class="copy" />
                    </span>
                </div>

                <!-- 更新按鈕 -->
                <div class="shareCard-actions">
                    <button class="cancel-btn" @click="showShareModal = false">
                        取消
                    </button>
                    <button class="confirm-btn" @click="updateShareSettings()" :disabled="!shareCardCollectName.trim()">
                        更新
                    </button>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup>
import { ref, reactive, computed, onMounted, onBeforeUnmount } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { Message } from 'view-ui-plus'
import { BlobServiceClient } from '@azure/storage-blob'
import api from '@/api'
import profile from '@/api/profile'
import confProfile from '@/api/profile'
import LoginNavbar from './components/LoginNavbar.vue'
import SvgIcon from "../components/SvgIcon.vue";

// 取得當前環境配置
const serverLocation = api.main.getServerLocation()
const blobDomainUrl = profile.blobDomain[serverLocation]

// 路由
const route = useRoute()
const router = useRouter()

// i18n
const { t } = useI18n()

// 響應式狀態
const loading = ref(true)
const cardCollectInfo = ref(null)
const cardList = ref(null)
const userLoggedIn = ref(false)
const tokenRefreshInterval = ref(null)

// 卡片選單相關
const showCardMenu = ref(false)
const currentCard = ref(null)
const cardMenuPos = reactive({ x: 0, y: 0 })

// 分享設定相關
const showShareModal = ref(false)
const shareCardCollectName = ref('')
const shareOpenSubmit = ref(true)
const currentShareURL = ref('')
const shortURLHost = ref(confProfile.shortUrlHost[serverLocation])

// 上傳表單相關
const showUploadModal = ref(false)
const uploading = ref(false)
const uploadProgress = ref(0)
const uploadStatus = ref('準備上傳...')
const isDragOver = ref(false)
const maxFileSize = ref(5) // MB

const uploadForm = reactive({
    cardName: '',
    selectedFile: null,
    textContent: '',
    groupId: '', // 初始為空，等載入完成後設定
    cardColor: '#FFFFFF', // 預設顏色(白色)
    errors: {},
    generalError: ''
})

const currentUser = ref(null)

// 顏色預設選項
const colorPresets = [
    { name: '白色(預設)', value: '#FFFFFF' },
    { name: '淺黃', value: '#FFF4CD' },
    { name: '淺綠', value: '#D1F2EB' },
    { name: '淺藍', value: '#C9E4FF' },
    { name: '淺粉', value: '#FFD1DC' }
]

// refs
const loginNavbar = ref(null)
const fileInput = ref(null)

// 編輯相關
const showEditModal = ref(false)
const cardToEdit = ref(null)
const isEditing = ref(false)
const isEditDragOver = ref(false)

// 編輯表單
const editForm = reactive({
    cardName: '',
    textContent: '',
    groupId: '',
    cardColor: '#FFFFFF', // 預設顏色(白色)
    originalFile: null, // 原始檔案資訊
    newFile: null, // 新選擇的檔案
    removeOriginalFile: false, // 是否移除原始檔案
    errors: {},
    generalError: ''
})

// refs
const editFileInput = ref(null)

// 刪除確認相關
const showDeleteConfirm = ref(false)
const cardToDelete = ref(null)

// 分組相關
const selectedGroupId = ref(null) // 初始不設定，等載入完成後設定
const showAddGroupModal = ref(false)
const isAddingGroup = ref(false)

// 新增分組表單
const newGroupForm = reactive({
    groupName: '',
    errors: {},
    generalError: ''
})

// 圖片預覽相關
const showImagePreview = ref(false)
const previewCard = ref(null)
const imageErrorMap = ref({})

// 拖拽相關
const draggedCard = ref(null)
const isDragOverGroup = ref(null)

// 權限相關
const isCollectCreator = ref(false)

// 搜尋相關
const searchText = ref('')
const searchResults = ref(null)
const isSearching = ref(false)
const searchDebounceTimer = ref(null)

// 顏色篩選相關
const showColorFilter = ref(false)
const selectedColorFilter = ref(null)

// 留言相關變數
const commentInputs = reactive({}) // 每張卡片的留言輸入內容
const commentInputFocused = reactive({}) // 每張卡片的輸入框聚焦狀態
const cardComments = ref([]) // 所有留言資料
const activeCommentMenu = ref(null) // 當前顯示選單的留言ID
const commentLoading = ref(false) // 留言操作載入狀態

// 檔案上傳相關變數
const commentAttachments = reactive({}) // 每張卡片的附件列表
const isUploadingFiles = reactive({}) // 每張卡片的檔案上傳狀態
const isSubmittingComment = reactive({}) // 每張卡片的留言提交狀態（防抖用）

// 匿名用戶身份管理
const getAnonymousUserId = () => {
    let anonymousId = sessionStorage.getItem('anonymous_user_id')
    if (!anonymousId) {
        // 生成基於時間戳和隨機數的唯一 ID
        anonymousId = `anonymous_${Date.now()}_${Math.random().toString(36).substring(2, 11)}`
        sessionStorage.setItem('anonymous_user_id', anonymousId)
        console.log('生成新的匿名用戶 ID:', anonymousId)
    }
    return anonymousId
}

// 獲取當前用戶 ID
const getCurrentUserId = () => {
    // 優先從 LoginNavbar 獲取登入用戶
    if (loginNavbar.value?.isLogin && loginNavbar.value.profile) {
        return loginNavbar.value.profile.id || loginNavbar.value.profile.teammodelId
    }
    
    // 從 localStorage 獲取
    try {
        const profile = JSON.parse(localStorage.getItem("profile") || "{}")
        if (profile.id || profile.teammodelId) {
            return profile.id || profile.teammodelId
        }
    } catch (error) {
        console.error('解析用戶資料失敗:', error)
    }
    
    // 匿名用戶：使用 session ID
    return getAnonymousUserId()
}

// 獲取當前用戶名稱
const getCurrentUserName = () => {
    // 優先從 LoginNavbar 獲取登入用戶
    if (loginNavbar.value?.isLogin && loginNavbar.value.profile) {
        return loginNavbar.value.profile.name || t('htwbcard.collect.userIdentity.loggedInUser')
    }
    
    // 從 localStorage 獲取
    try {
        const profile = JSON.parse(localStorage.getItem("profile") || "{}")
        if (profile.name) {
            return profile.name
        }
    } catch (error) {
        console.error('解析用戶資料失敗:', error)
    }
    
    // 匿名用戶
    return t('htwbcard.collect.userIdentity.anonymousUser')
}

// 獲取當前用戶頭像
const getCurrentUserPicture = () => {
    // 優先從 LoginNavbar 獲取登入用戶
    if (loginNavbar.value?.isLogin && loginNavbar.value.profile) {
        return loginNavbar.value.profile.picture || ''
    }
    
    // 從 localStorage 獲取
    try {
        const profile = JSON.parse(localStorage.getItem("profile") || "{}")
        if (profile.picture) {
            return profile.picture
        }
    } catch (error) {
        console.error('解析用戶資料失敗:', error)
    }
    
    // 匿名用戶：返回空字符串，由前端組件使用SVG圖標
    return ''
}

// 檢查當前用戶是否可以編輯/刪除該卡片
const canEditCard = (card) => {
    // 如果是收集板創建者，可以編輯所有卡片
    if (isCollectCreator.value) {
        return true
    }
    
    // 檢查是否為卡片擁有者
    const currentUserId = getCurrentUserId()
    const cardOwnerId = card.owner?.id
    
    console.log('權限檢查:', { currentUserId, cardOwnerId, canEdit: cardOwnerId === currentUserId })
    return cardOwnerId === currentUserId
}

// 檢查卡片是否為當前用戶所有（用於視覺化標示）
const isMyCard = (card) => {
    const currentUserId = getCurrentUserId()
    const cardOwnerId = card.owner?.id
    return cardOwnerId === currentUserId
}

// 計算屬性
const canSubmit = computed(() => {
    const hasName = uploadForm.cardName.trim()
    const hasFile = uploadForm.selectedFile
    const hasText = uploadForm.textContent.trim()
    
    // 至少要有卡片名稱和（文字或檔案其中之一）
    return hasName && (hasText || hasFile)
})

// 編輯表單提交條件
const canSubmitEdit = computed(() => {
    const hasName = editForm.cardName.trim()
    const hasText = editForm.textContent.trim()
    const hasOriginalFile = editForm.originalFile && !editForm.removeOriginalFile
    const hasNewFile = editForm.newFile
    
    // 至少要有卡片名稱和（文字或檔案其中之一）
    return hasName && (hasText || hasOriginalFile || hasNewFile)
})

// 檢查是否有登入用戶
const hasLoginUser = computed(() => {
    // 檢查 LoginNavbar 組件的登入狀態或本地的 userLoggedIn 狀態
    return loginNavbar.value?.isLogin || userLoggedIn.value
})

// 根據分組 ID 獲取該分組的卡片
const getCardsByGroup = (groupId) => {
    if (!cardList.value) return []
    return cardList.value.filter(card => {
        // 如果卡片沒有 groupId，視為屬於第一個分組
        const cardGroup = card.groupId || (cardCollectInfo.value?.groups?.[0]?.groupId)
        return cardGroup === groupId
    })
}

// 根據搜尋狀態獲取要顯示的卡片
const getDisplayCards = (groupId) => {
    let cards = []
    
    if (searchResults.value && searchText.value) {
        // 有搜尋結果時，過濾出屬於此分組的搜尋結果
        cards = searchResults.value.filter(card => {
            const cardGroup = card.groupId || (cardCollectInfo.value?.groups?.[0]?.groupId)
            return cardGroup === groupId
        })
    } else {
        // 沒有搜尋時，顯示原本的分組卡片
        cards = getCardsByGroup(groupId)
    }
    
    // 應用顏色篩選
    if (selectedColorFilter.value) {
        cards = cards.filter(card => {
            const cardColor = card.cardColor || '#FFFFFF' // 預設白色
            return cardColor === selectedColorFilter.value
        })
    }
    
    return cards
}


// 搜尋輸入處理（防抖）
const onSearchInput = () => {
    if (searchDebounceTimer.value) {
        clearTimeout(searchDebounceTimer.value)
    }
    
    // 如果輸入框被清空，立即清除搜尋結果
    if (!searchText.value.trim()) {
        clearSearch()
        return
    }
    
    // 延遲搜尋，但不顯示載入狀態（避免閃爍）
    searchDebounceTimer.value = setTimeout(() => {
        if (searchText.value.trim()) {
            performSearch()
        }
    }, 300) // 減少防抖時間至 300ms
}

// 執行搜尋
const performSearch = async () => {
    let trimmedText = searchText.value.trim()
    if (!trimmedText) {
        clearSearch()
        return
    }

    if (isSearching.value) return

    isSearching.value = true

    try {
        const collectId = route.params.id || route.query.id
        const searchData = {
            searchText: trimmedText, // 直接使用原始文字，讓後端處理雙向搜尋
            collectId: collectId, // 可選，限制在當前收集板內搜尋
            sortOrder: 'desc',
            sortField: 'updateTime'
        }

        const response = await api.cardAPI.searchCards(searchData)
        console.log('搜尋結果:', response)

        // 檢查搜尋文字是否還一致（避免快速輸入造成的結果錯亂）
        if (trimmedText === searchText.value.trim()) {
            if (response && response.data) {
                searchResults.value = response.data
            } else {
                searchResults.value = []
            }
        }
    } catch (error) {
        console.error('搜尋失敗:', error)
        // 只有在當前搜尋文字一致時才顯示錯誤
        if (trimmedText === searchText.value.trim()) {
            Message.error(t('htwbcard.messages.searchFailed'))
            searchResults.value = []
        }
    } finally {
        isSearching.value = false
    }
}

// 清除搜尋
const clearSearch = () => {
    searchText.value = ''
    searchResults.value = null
    isSearching.value = false // 清除載入狀態
    if (searchDebounceTimer.value) {
        clearTimeout(searchDebounceTimer.value)
        searchDebounceTimer.value = null
    }
}

// 顏色篩選相關函數
const toggleColorFilter = () => {
    showColorFilter.value = !showColorFilter.value
}

const selectColorFilter = (colorValue) => {
    selectedColorFilter.value = colorValue
    showColorFilter.value = false
}

const clearColorFilter = () => {
    selectedColorFilter.value = null
    showColorFilter.value = false
}


// 處理登入成功
const onLoginSuccess = (profileData) => {
    console.log('用戶登入成功:', profileData)
    userLoggedIn.value = true
    // 確保狀態同步，強制重新計算 computed
    console.log('Login state updated, hasLoginUser:', hasLoginUser.value)
    
    // 重新檢查是否為創建者
    if (cardCollectInfo.value && cardCollectInfo.value.creator && profileData) {
        const currentUserId = profileData.id || profileData.teammodelId
        isCollectCreator.value = cardCollectInfo.value.creator.id === currentUserId
        console.log('Login success - isCollectCreator updated:', isCollectCreator.value)
    }
}

// 處理登出成功
const onLogoutSuccess = () => {
    console.log('用戶登出成功')
    userLoggedIn.value = false
    isCollectCreator.value = false // 登出後清除創建者權限
    // 登出後跳轉回 HTwbCard 首頁 (HTwbCardHome)
    router.push('/htwb-card-home')
}

// 開啟卡片選單
const openCardMenu = (card, event) => {
    event.stopPropagation()
    currentCard.value = card
    cardMenuPos.x = event.clientX - 80
    cardMenuPos.y = event.clientY + 10
    showCardMenu.value = true
}

// 關閉卡片選單
const closeCardMenu = () => {
    showCardMenu.value = false
    currentCard.value = null
}

// 開啟刪除確認視窗
const openDeleteConfirm = () => {
    if (currentCard.value) {
        cardToDelete.value = currentCard.value
        showDeleteConfirm.value = true
        closeCardMenu()
    }
}

// 關閉刪除確認視窗
const closeDeleteConfirm = () => {
    showDeleteConfirm.value = false
    cardToDelete.value = null
}

// 刪除狀態
const isDeleting = ref(false)

// 開啟編輯 Modal
const openEditModal = () => {
    if (currentCard.value) {
        cardToEdit.value = currentCard.value
        
        // 初始化編輯表單
        editForm.cardName = currentCard.value.cardName || ''
        editForm.textContent = currentCard.value.textContent || ''
        editForm.cardColor = currentCard.value.cardColor || '#FFFFFF' // 載入卡片顏色
        editForm.groupId = currentCard.value.groupId || (cardCollectInfo.value?.groups?.[0]?.groupId) || ''
        
        // 檔案資訊
        if (currentCard.value.filePath) {
            editForm.originalFile = {
                id: currentCard.value.id,
                cardName: currentCard.value.cardName,
                filePath: currentCard.value.filePath,
                fileType: currentCard.value.fileType
            }
        } else {
            editForm.originalFile = null
        }
        
        editForm.newFile = null
        editForm.removeOriginalFile = false
        editForm.errors = {}
        editForm.generalError = ''
        
        showEditModal.value = true
        closeCardMenu()
    }
}

// 關閉編輯 Modal
const closeEditModal = () => {
    if (isEditing.value) return // 編輯中不允許關閉
    
    showEditModal.value = false
    resetEditForm()
}

// 重置編輯表單
const resetEditForm = () => {
    cardToEdit.value = null
    editForm.cardName = ''
    editForm.textContent = ''
    editForm.cardColor = '#FFFFFF' // 重置為預設顏色(白色)
    editForm.groupId = ''
    editForm.originalFile = null
    editForm.newFile = null
    editForm.removeOriginalFile = false
    editForm.errors = {}
    editForm.generalError = ''
    isEditing.value = false
    isEditDragOver.value = false
    if (editFileInput.value) {
        editFileInput.value.value = ''
    }
}

// 編輯檔案選擇處理
const handleEditFileSelect = (event) => {
    const file = event.target.files[0]
    if (file) {
        validateAndSetEditFile(file)
    }
}

// 編輯檔案拖拽處理
const handleEditFileDragOver = (event) => {
    event.preventDefault()
    event.dataTransfer.dropEffect = 'copy'
}

const handleEditFileDragEnter = (event) => {
    event.preventDefault()
    isEditDragOver.value = true
}

const handleEditFileDragLeave = (event) => {
    event.preventDefault()
    if (!event.currentTarget.contains(event.relatedTarget)) {
        isEditDragOver.value = false
    }
}

const handleEditFileDrop = (event) => {
    event.preventDefault()
    isEditDragOver.value = false
    
    const files = event.dataTransfer.files
    if (files.length > 0) {
        validateAndSetEditFile(files[0])
    }
}

// 驗證和設定編輯檔案
const validateAndSetEditFile = (file) => {
    editForm.errors.file = ''
    editForm.generalError = ''

    // 檔案大小驗證
    const maxSizeBytes = maxFileSize.value * 1024 * 1024
    if (file.size > maxSizeBytes) {
        editForm.errors.file = t('htwbcard.collect.errors.fileSizeExceeded', { size: maxFileSize.value })
        return
    }

    editForm.newFile = file
    
    // 清除相關錯誤訊息
    if (editForm.errors.textContent) {
        delete editForm.errors.textContent
    }
    if (editForm.errors.file) {
        delete editForm.errors.file
    }
    editForm.generalError = ''
    
    console.log("Selected edit file:", file)
}

// 移除編輯檔案
const removeEditFile = () => {
    if (editForm.newFile && isImageFile(editForm.newFile)) {
        const previewUrl = getFilePreviewUrl(editForm.newFile)
        if (previewUrl) {
            URL.revokeObjectURL(previewUrl)
        }
    }
    
    editForm.newFile = null
    editForm.errors.file = ''
    if (editFileInput.value) {
        editFileInput.value.value = ''
    }
}

// 移除原始檔案
const removeOriginalFile = () => {
    editForm.removeOriginalFile = true
    editForm.originalFile = null // 清除原始檔案資訊，立即更新 UI
    
    // 清除檔案相關錯誤
    if (editForm.errors.file) {
        delete editForm.errors.file
    }
    
    console.log('原始檔案已標記為移除，將變成純文字卡片')
}

// 預覽編輯的圖片
const previewEditImage = () => {
    if (!editForm.newFile || !isImageFile(editForm.newFile)) {
        return
    }
    
    const tempCard = {
        id: 'temp-edit-preview',
        cardName: editForm.newFile.name,
        fileType: getFileType(editForm.newFile),
        tempImageUrl: getFilePreviewUrl(editForm.newFile)
    }
    
    previewCard.value = tempCard
    showImagePreview.value = true
    document.body.style.overflow = 'hidden'
}

// 驗證編輯表單
const validateEditForm = () => {
    editForm.errors = {}
    editForm.generalError = ''
    let isValid = true

    // 驗證卡片名稱
    if (!editForm.cardName.trim()) {
        editForm.errors.cardName = t('htwbcard.collect.errors.cardNameRequired')
        isValid = false
    } else if (editForm.cardName.trim().length > 50) {
        editForm.errors.cardName = t('htwbcard.collect.errors.cardNameTooLong')
        isValid = false
    }

    // 驗證內容：至少要有文字或檔案其中之一
    const hasText = editForm.textContent.trim()
    const hasOriginalFile = editForm.originalFile && !editForm.removeOriginalFile
    const hasNewFile = editForm.newFile
    
    if (!hasText && !hasOriginalFile && !hasNewFile) {
        editForm.errors.textContent = t('htwbcard.collect.errors.textOrFileRequired')
        editForm.errors.file = t('htwbcard.collect.errors.textOrFileRequired')
        isValid = false
    }

    return isValid
}

// 更新卡片
const updateCard = async () => {
    if (!validateEditForm()) {
        return
    }

    if (isEditing.value) return

    isEditing.value = true
    editForm.generalError = ''

    try {
        getCurrentUser()
        
        // 驗證必要資料
        const userId = getCurrentUserId()
        const userName = getCurrentUserName()
        
        if (!userId || !userName) {
            throw new Error(t('htwbcard.collect.errors.missingUserInfo'))
        }
        
        const collectId = route.params.id || route.query.id
        const cardId = cardToEdit.value.id
        
        if (!collectId || !cardId) {
            throw new Error(t('htwbcard.collect.errors.missingIdInfo'))
        }
        
        console.log('用戶資訊:', { userId, userName })
        console.log('卡片資訊:', { cardId, collectId })
        
        // 準備更新資料 - 只包含必填欄位
        const updateData = {
            id: String(cardId),
            collectId: String(collectId),
            owner: {
                id: String(userId),
                name: String(userName)
            }
        }

        // 只有當值有變更時才加入更新資料
        if (editForm.cardName.trim()) {
            updateData.cardName = editForm.cardName.trim()
        }
        
        if (editForm.textContent.trim()) {
            updateData.textContent = editForm.textContent.trim()
        } else {
            // 如果文字內容被清空，明確設為空字串
            updateData.textContent = ''
        }

        // 如果有選擇分組
        if (editForm.groupId) {
            updateData.groupId = editForm.groupId
        }

        // 更新卡片顏色
        if (editForm.cardColor) {
            updateData.cardColor = editForm.cardColor
        }

        // 處理檔案更新邏輯
        if (editForm.newFile) {
            // 有新檔案：上傳新檔案並更新路徑
            const originalFileName = editForm.newFile.name
            const timestamp = new Date().getTime()
            const fileName = `${timestamp}_${originalFileName}`
            const filePath = `${collectId}/${cardId}/${fileName}`
            
            // 上傳新檔案
            await uploadFileToBlob(filePath, editForm.newFile)
            
            updateData.filePath = filePath
            updateData.fileType = getFileType(editForm.newFile)
        } else if (editForm.removeOriginalFile) {
            // 移除檔案：清空檔案路徑
            updateData.filePath = ''
            updateData.fileType = 'text/plain'
        }
        // 如果沒有新檔案且不移除原檔案，則不傳送 filePath 和 fileType

        // 呼叫更新 API
        console.log('準備更新卡片，發送資料:', updateData)
        const response = await api.cardAPI.updateCard(updateData)
        console.log("Card updated response:", response)
        console.log("Response type:", typeof response)
        console.log("Response keys:", Object.keys(response || {}))
        
        // 根據後端 API 實作，成功時會回傳卡片物件
        if (response && (response.existingCard || response.updated !== false)) {
            Message.success(t('htwbcard.collect.successMessages.cardUpdated'))
            
            // 重新載入卡片列表
            await getCardCollectData()
            
            // 重置編輯狀態並關閉 Modal
            isEditing.value = false
            showEditModal.value = false
            resetEditForm()
        } else {
            throw new Error(`更新請求失敗: ${JSON.stringify(response)}`)
        }
        
    } catch (error) {
        console.error("Error updating card:", error)
        console.error("Error response:", error.response?.data)
        console.error("Current user:", currentUser.value)
        console.error("Edit form data:", editForm)
        
        let errorMessage = t('htwbcard.collect.errors.generalError')
        if (error.response) {
            // HTTP 錯誤回應
            const status = error.response.status
            const data = error.response.data
            
            if (status === 400) {
                errorMessage = `請求資料有誤: ${data || '必填欄位有誤，請核對!'}`
            } else if (status === 404) {
                errorMessage = t('htwbcard.collect.errors.cardNotFound')
            } else if (status === 403) {
                errorMessage = t('htwbcard.collect.errors.noPermission')
            } else if (status === 401) {
                errorMessage = t('htwbcard.collect.errors.authFailed')
            } else {
                errorMessage = `更新失敗 (${status}): ${data || error.message}`
            }
        } else if (error.message) {
            if (error.message.includes('SAS Token')) {
                errorMessage = t('htwbcard.collect.errors.sasTokenExpired')
            } else if (error.message.includes('檔案上傳失敗')) {
                errorMessage = t('htwbcard.collect.errors.fileUploadFailed')
            } else {
                errorMessage = error.message
            }
        }
        
        editForm.generalError = errorMessage
        Message.error(errorMessage)
    } finally {
        isEditing.value = false
    }
}

// 確認刪除卡片
const confirmDelete = async () => {
    if (!cardToDelete.value || isDeleting.value) return
    
    isDeleting.value = true
    
    try {
        console.log('開始刪除卡片:', cardToDelete.value)
        
        // 呼叫刪除 API
        const response = await api.cardAPI.deleteCard({
            id: cardToDelete.value.id
        })
        
        console.log('刪除 API 回應:', response)
        
        if (response && response.status === 200) {
            Message.success(t('htwbcard.collect.successMessages.cardDeleted'))
            closeDeleteConfirm()
            
            // 重新載入卡片列表
            await getCardCollectData()
        } else {
            throw new Error(t('htwbcard.collect.errors.generalError'))
        }
    } catch (error) {
        console.error('刪除卡片失敗:', error)
        Message.error(t('htwbcard.collect.errors.generalError'))
    } finally {
        isDeleting.value = false
    }
}

// 獲取卡片 SAS token
const getCardCollectSas = async () => {
    try {
        const res = await api.cardAPI.getCardCollectSas()
        console.log("Card SAS response:", res)
        
        // 儲存 SAS token 和過期時間
        sessionStorage.setItem("card_sas_token", res.token)
        sessionStorage.setItem("card_sas_expiry", res.expiresOn)
        
        return res
    } catch (error) {
        console.error("Error fetching card collect SAS:", error)
    }
}

// 啟動 Card SAS token 自動刷新機制
const startCardSasRefreshInterval = () => {
    tokenRefreshInterval.value = setInterval(async () => {
        const now = Date.now()
        const refreshThreshold = 10 * 1000 // 提前 10 秒刷新
        
        // 檢查 Card SAS Token 是否即將過期
        const cardSasExpiry = sessionStorage.getItem("card_sas_expiry")
        if (cardSasExpiry) {
            const sasExpiryMs = Date.parse(cardSasExpiry)
            if (sasExpiryMs - now < refreshThreshold) {
                console.log("Card SAS Token is about to expire. Refreshing...")
                await getCardCollectSas()
            }
        }
    }, 60 * 1000) // 每分鐘檢查一次
}

// 獲取卡片收集資料
const getCardCollectData = async () => {
    try {
        console.log("Current route:", route)
        console.log("Route params:", route.params)
        console.log("Route query:", route.query)
        
        const cid = route.params.id || route.query.id // 從路由參數取得收集夾 ID
        console.log("Extracted cid:", cid)
        
        if (!cid) {
            console.error("No card collect ID provided, route query:", route.query, "route params:", route.params)
            loading.value = false
            return
        }
        
        const data = {
            cid: cid,
            returnCardList: true // 同時獲取卡片列表
        }
        
        console.log("Calling getCardCollect with data:", data)
        const res = await api.cardAPI.getCardCollect(data)
        console.log("Card collect response:", res)
        
        cardCollectInfo.value = res.cardCollect
        cardList.value = res.cardList
        
        // 提取所有卡片的留言資料
        if (res.cardList && Array.isArray(res.cardList)) {
            let allComments = []
            res.cardList.forEach(card => {
                if (card.comments && Array.isArray(card.comments)) {
                    allComments = allComments.concat(card.comments)
                }
            })
            cardComments.value = allComments
            console.log('載入留言資料:', allComments)
        } else {
            cardComments.value = []
        }
        
        // 檢查是否為收集板創建者
        if (res.cardCollect && res.cardCollect.creator) {
            // 檢查當前用戶資訊
            const savedProfile = localStorage.getItem("profile")
            if (savedProfile) {
                try {
                    const profileData = JSON.parse(savedProfile)
                    const currentUserId = profileData.id || profileData.teammodelId
                    isCollectCreator.value = res.cardCollect.creator.id === currentUserId
                    console.log('isCollectCreator:', isCollectCreator.value, 'creator:', res.cardCollect.creator.id, 'current:', currentUserId)
                } catch (error) {
                    console.error('解析用戶資料失敗:', error)
                    isCollectCreator.value = false
                }
            } else {
                isCollectCreator.value = false
            }
        }
        
        // 初始化圖片狀態
        if (res.cardList && Array.isArray(res.cardList)) {
            res.cardList.forEach(card => {
                if (card.id && isImageFileType(card.fileType)) {
                    imageErrorMap.value[card.id] = false
                }
            })
        }
        
        // 處理分組資料，確保沒有重複且至少有一個預設分組
        if (!res.cardCollect.groups || res.cardCollect.groups.length === 0) {
            // 如果沒有分組，建立預設分組
            res.cardCollect.groups = [
                {
                    groupId: 'group-1',
                    groupName: t('htwbcard.collect.defaultGroup'),
                    members: []
                }
            ]
        } else {
            // 如果有分組，進行去重處理（基於 groupId 和 groupName）
            const uniqueGroups = []
            const seenGroupIds = new Set()
            const seenGroupNames = new Set()
            
            for (const group of res.cardCollect.groups) {
                if (!seenGroupIds.has(group.groupId) && !seenGroupNames.has(group.groupName)) {
                    uniqueGroups.push(group)
                    seenGroupIds.add(group.groupId)
                    seenGroupNames.add(group.groupName)
                }
            }
            
            res.cardCollect.groups = uniqueGroups
        }
        
        cardCollectInfo.value = res.cardCollect
        
        // 設定預設選擇的分組（選擇第一個分組）
        if (res.cardCollect?.groups?.length > 0 && !selectedGroupId.value) {
            selectedGroupId.value = res.cardCollect.groups[0].groupId
            // 同時設定上傳表單的預設分組
            if (!uploadForm.groupId) {
                uploadForm.groupId = res.cardCollect.groups[0].groupId
            }
        }
        
        loading.value = false
    } catch (error) {
        console.error("Error fetching card collect data:", error)
        console.error("Error details:", error.response || error)
        loading.value = false
    }
}

// 分享相關方法
const openShareModal = () => {
    if (cardCollectInfo.value) {
        shareCardCollectName.value = cardCollectInfo.value.cardCollectName
        shareOpenSubmit.value = cardCollectInfo.value.openSubmit
        currentShareURL.value = cardCollectInfo.value.urlClipCode || ""
        showShareModal.value = true
    }
}

const copyShareUrl = () => {
    if (currentShareURL.value) {
        const fullUrl = shortURLHost.value + currentShareURL.value
        navigator.clipboard.writeText(fullUrl).then(() => {
            Message.success(t('htwbcard.messages.alreadyCopied'))
        }).catch(() => {
            Message.error(t('htwbcard.collect.errors.copyFailed'))
        })
    }
}

// 留言相關方法
const getCardComments = (cardId) => {
    if (!cardComments.value) return []
    return cardComments.value.filter(comment => 
        comment.cardId === cardId && !comment.isDeleted
    ).sort((a, b) => b.time - a.time) // 按時間降序排列，最新在前
}

const onCommentInputFocus = (cardId) => {
    // 初始化該卡片的輸入框
    if (!commentInputs[cardId]) {
        commentInputs[cardId] = ''
    }
    // 設定聚焦狀態
    commentInputFocused[cardId] = true
}

const onCommentInputBlur = (cardId) => {
    // 失去焦點時，如果沒有內容則清除聚焦狀態
    if (!commentInputs[cardId] || !commentInputs[cardId].trim()) {
        commentInputFocused[cardId] = false
    }
}

// ===== 檔案上傳相關方法 =====


// 觸發檔案選擇
const triggerFileUpload = (cardId) => {
    const fileInput = document.getElementById(`fileInput-${cardId}`)
    if (fileInput) {
        fileInput.click()
    }
}

// 處理留言檔案選擇
const handleCommentFileSelect = async (cardId, event) => {
    const files = event.target.files
    if (!files || files.length === 0) return
    
    // 初始化該卡片的附件陣列
    if (!commentAttachments[cardId]) {
        commentAttachments[cardId] = []
    }
    
    for (const file of files) {
        // 檔案大小檢查 (5MB)
        if (file.size > 5 * 1024 * 1024) {
            Message.error(t('htwbcard.collect.comments.fileTooLarge', { fileName: file.name }))
            continue
        }
        
        // 建立檔案預覽物件
        const fileObj = {
            id: Date.now() + Math.random(),
            name: file.name,
            size: file.size,
            type: file.type,
            file: file,
            uploading: false,
            progress: 0,
            uploaded: false,
            filePath: null,
            preview: null
        }
        
        // 為圖片建立預覽
        if (file.type.startsWith('image/')) {
            fileObj.preview = URL.createObjectURL(file)
        }
        
        commentAttachments[cardId].push(fileObj)
    }
    
    // 清空檔案輸入框
    event.target.value = ''
}

// 移除附件
const removeAttachment = (cardId, index) => {
    if (commentAttachments[cardId] && commentAttachments[cardId][index]) {
        const file = commentAttachments[cardId][index]
        
        // 釋放預覽 URL 記憶體
        if (file.preview && file.preview.startsWith('blob:')) {
            URL.revokeObjectURL(file.preview)
        }
        
        commentAttachments[cardId].splice(index, 1)
        
        // 如果沒有附件了，清空陣列
        if (commentAttachments[cardId].length === 0) {
            delete commentAttachments[cardId]
        }
    }
}

// 統一的 Azure Blob Storage 上傳函數
const uploadToAzureBlob = async (file, filePath, options = {}) => {
    // 檔案大小限制檢查 (5MB)
    const maxFileSize = 5 * 1024 * 1024 // 5MB
    if (file.size > maxFileSize) {
        throw new Error(t('htwbcard.collect.comments.fileTooLarge', { fileName: file.name || 'unknown' }))
    }
    
    const sasToken = sessionStorage.getItem("card_sas_token")
    if (!sasToken) {
        throw new Error(t('htwbcard.collect.errors.sasTokenExpired'))
    }

    try {
        const containerName = "ht-sharing-card"
        const blobServiceUrl = `${blobDomainUrl}?${sasToken}`
        const blobServiceClient = new BlobServiceClient(blobServiceUrl)
        const containerClient = blobServiceClient.getContainerClient(containerName)
        const blobClient = containerClient.getBlobClient(filePath)
        const blockBlobClient = blobClient.getBlockBlobClient()
        
        // 設定上傳選項
        const uploadOptions = {
            blobHTTPHeaders: {
                blobContentType: file.type || 'application/octet-stream'
            },
            onProgress: options.onProgress || null
        }
        
        // 執行上傳
        const response = await blockBlobClient.uploadData(file, uploadOptions)
        
        if (response._response.status !== 201) {
            throw new Error(`上傳失敗，狀態碼: ${response._response.status}`)
        }
        
        console.log('檔案上傳成功:', filePath)
        return response
        
    } catch (error) {
        console.error('檔案上傳失敗:', error)
        throw error
    }
}

// 留言檔案上傳（使用統一函數）
const uploadCommentFileToBlob = async (cardId, fileObj, collectId) => {
    try {
        fileObj.uploading = true
        fileObj.progress = 0
        
        // 建立檔案路徑：collectId/cardId/timestamp_filename
        const timestamp = Date.now()
        const fileName = `${timestamp}_${fileObj.name}`
        const filePath = `${collectId}/${cardId}/${fileName}`
        
        // 使用統一的上傳函數
        await uploadToAzureBlob(fileObj.file, filePath, {
            onProgress: (progress) => {
                if (progress.loadedBytes && fileObj.size) {
                    fileObj.progress = Math.round((progress.loadedBytes / fileObj.size) * 100)
                }
            }
        })
        
        fileObj.uploaded = true
        fileObj.uploading = false
        fileObj.progress = 100
        fileObj.filePath = filePath
        
        return filePath
        
    } catch (error) {
        fileObj.uploading = false
        fileObj.progress = 0
        throw error
    }
}

// 取得檔案類型圖標
const getFileIcon = (mimeType) => {
    if (!mimeType) return 'doc'
    
    if (mimeType.startsWith('image/')) return 'img_upload'
    if (mimeType.startsWith('video/')) return 'play'
    if (mimeType.startsWith('audio/')) return 'microphone'
    if (mimeType.includes('pdf')) return 'doc'
    if (mimeType.includes('word') || mimeType.includes('document')) return 'doc'
    if (mimeType.includes('excel') || mimeType.includes('spreadsheet')) return 'file-right'
    if (mimeType.includes('powerpoint') || mimeType.includes('presentation')) return 'file-right'
    
    return 'doc'
}

// 根據檔案路徑獲取檔案圖標
const getFileIconByPath = (filePath) => {
    if (!filePath) return 'doc'
    
    const extension = filePath.toLowerCase().split('.').pop()
    
    // 圖片檔案
    if (['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg'].includes(extension)) {
        return 'img_upload'
    }
    // 影片檔案
    if (['mp4', 'avi', 'mov', 'wmv', 'flv', 'webm', 'mkv'].includes(extension)) {
        return 'play'
    }
    // 音訊檔案
    if (['mp3', 'wav', 'flac', 'aac', 'ogg', 'm4a'].includes(extension)) {
        return 'microphone'
    }
    // PDF
    if (extension === 'pdf') {
        return 'doc'
    }
    // Word 文件
    if (['doc', 'docx'].includes(extension)) {
        return 'doc'
    }
    // Excel 文件
    if (['xls', 'xlsx'].includes(extension)) {
        return 'file-right'
    }
    // PowerPoint 文件
    if (['ppt', 'pptx'].includes(extension)) {
        return 'file-right'
    }
    // 文字檔案
    if (['txt', 'md', 'rtf'].includes(extension)) {
        return 'doc'
    }
    
    return 'doc'
}


// 判斷是否為圖片檔案 (根據路徑)
const isImageFileByPath = (filePath) => {
    if (!filePath) return false
    const extension = filePath.toLowerCase().split('.').pop()
    return ['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg'].includes(extension)
}

const isVideoFileByPath = (filePath) => {
    if (!filePath) return false
    const extension = filePath.toLowerCase().split('.').pop()
    return ['mp4', 'avi', 'mov', 'wmv', 'webm', 'mkv', 'flv'].includes(extension)
}

const isAudioFileByPath = (filePath) => {
    if (!filePath) return false
    const extension = filePath.toLowerCase().split('.').pop()
    return ['mp3', 'wav', 'ogg', 'aac', 'flac', 'm4a', 'wma'].includes(extension)
}

// 從檔案路徑取得檔案名稱
const getFileNameFromPath = (filePath) => {
    if (!filePath) return '未知檔案'
    return filePath.split('/').pop()
}

// 取得附件的完整 URL（使用 sessionStorage 中的 SAS Token）
const getAttachmentUrl = (filePath) => {
    if (!filePath) return ''
    
    // 使用 sessionStorage 中的 SAS Token（與現有系統一致）
    const token = sessionStorage.getItem('card_sas_token')
    if (token) {
        return `${blobDomainUrl}/ht-sharing-card/${filePath}?${token}`
    }
    
    // 如果沒有 token，返回不帶 token 的 URL
    return `${blobDomainUrl}/ht-sharing-card/${filePath}`
}

// 開啟圖片預覽 (留言附件用) - 使用現有的圖片預覽系統
const openAttachmentImagePreview = (filePath, fileName) => {
    // 建立臨時的卡片物件來復用現有的圖片預覽功能
    const tempCard = {
        id: `attachment-${Date.now()}`,
        cardName: fileName || getFileNameFromPath(filePath),
        fileType: 'image',
        filePath: filePath
    }
    
    previewCard.value = tempCard
    showImagePreview.value = true
    document.body.style.overflow = 'hidden'
}

const submitComment = async (cardId) => {
    const content = commentInputs[cardId]?.trim()
    const attachments = commentAttachments[cardId] || []
    
    // 防抖處理：如果正在提交中，直接返回
    if (isSubmittingComment[cardId]) {
        console.log('留言正在提交中，忽略重複請求')
        return
    }
    
    // 至少要有內容或附件
    if (!content && attachments.length === 0) {
        console.log('留言需要至少包含內容或附件')
        return
    }

    try {
        commentLoading.value = true
        isUploadingFiles[cardId] = true
        isSubmittingComment[cardId] = true // 設定提交狀態
        
        // 上傳所有附件到 Azure Blob Storage
        const uploadedAttachments = []
        if (attachments.length > 0) {
            console.log(`開始上傳 ${attachments.length} 個附件...`)
            
            for (const fileObj of attachments) {
                if (!fileObj.uploaded) {
                    try {
                        const filePath = await uploadCommentFileToBlob(cardId, fileObj, cardCollectInfo.value?.id || 'default')
                        uploadedAttachments.push({
                            id: fileObj.id,
                            filePath: filePath,
                            uploadTime: Math.floor(Date.now() / 1000)
                        })
                    } catch (uploadError) {
                        console.error('檔案上傳失敗:', uploadError)
                        // 檔案上傳失敗時顯示具體錯誤訊息
                        if (uploadError.message.includes('fileSizeExceeded') || uploadError.message.includes('過大') || uploadError.message.includes('over')) {
                            Message.error(t('htwbcard.collect.comments.fileTooLarge', { fileName: fileObj.name }))
                        } else {
                            Message.error(t('htwbcard.collect.comments.fileUploadFailed', { fileName: fileObj.name }))
                        }
                        return // 如果有檔案上傳失敗，停止提交留言
                    }
                }
            }
        }

        const currentUserId = getCurrentUserId()
        const currentUserName = getCurrentUserName()
        const currentUserPicture = getCurrentUserPicture()

        // 準備留言資料
        const commentData = {
            cardId: cardId,
            content: content || '', // 如果只有附件，內容可以為空
            commentType: uploadedAttachments.length > 0 ? 'file' : 'text',
            attachments: uploadedAttachments,
            commenter: {
                id: currentUserId,
                name: currentUserName,
                picture: currentUserPicture
            }
        }

        console.log('提交留言資料:', commentData)

        // 呼叫 API 建立留言
        const response = await api.cardAPI.createComment(commentData)
        
        if (response.status === 200) {
            // 新增留言到本地狀態
            cardComments.value.push(response.data)
            
            // 清空輸入框和附件
            commentInputs[cardId] = ''
            commentInputFocused[cardId] = false
            
            // 清理附件預覽
            if (commentAttachments[cardId]) {
                commentAttachments[cardId].forEach(file => {
                    if (file.preview && file.preview.startsWith('blob:')) {
                        URL.revokeObjectURL(file.preview)
                    }
                })
                delete commentAttachments[cardId]
            }
            
            Message.success(t('htwbcard.collect.comments.published'))
        }
    } catch (error) {
        console.error('發布留言失敗:', error)
        Message.error(t('htwbcard.collect.comments.publishFailed'))
    } finally {
        commentLoading.value = false
        isUploadingFiles[cardId] = false
        isSubmittingComment[cardId] = false // 重置提交狀態
    }
}

const canEditComment = (comment) => {
    const currentUserId = getCurrentUserId()
    return comment.commenter?.id === currentUserId
}

const toggleCommentMenu = (commentId) => {
    activeCommentMenu.value = activeCommentMenu.value === commentId ? null : commentId
}

const editComment = (comment) => {
    comment.isEditing = true
    comment.editContent = comment.content
    // 複製現有附件到編輯狀態
    comment.editAttachments = comment.attachments ? [...comment.attachments] : []
    activeCommentMenu.value = null
}

const saveCommentEdit = async (comment) => {
    const newContent = comment.editContent?.trim()
    const editAttachments = comment.editAttachments || []
    
    // 檢查是否有內容或附件
    if (!newContent && editAttachments.length === 0) {
        Message.warning(t('htwbcard.collect.comments.contentRequired'))
        return
    }

    const currentUserId = getCurrentUserId()
    const currentUserName = getCurrentUserName() 
    const currentUserPicture = getCurrentUserPicture()

    const updateData = {
        commentId: comment.id,
        content: newContent,
        attachments: editAttachments,
        updater: {
            id: currentUserId,
            name: currentUserName,
            picture: currentUserPicture
        }
    }

    try {
        commentLoading.value = true
        const response = await api.cardAPI.updateComment(updateData)
        
        if (response.status === 200) {
            // 更新本地狀態
            const index = cardComments.value.findIndex(c => c.id === comment.id)
            if (index !== -1) {
                cardComments.value[index] = response.data
            }
            comment.isEditing = false
            // 清理編輯狀態
            comment.editAttachments = []
            Message.success(t('htwbcard.collect.comments.updated'))
        }
    } catch (error) {
        console.error('更新留言失敗:', error)
        Message.error(t('htwbcard.collect.comments.updateFailed'))
    } finally {
        commentLoading.value = false
    }
}

const cancelCommentEdit = (comment) => {
    comment.isEditing = false
    comment.editContent = ''
    // 清理編輯附件狀態
    comment.editAttachments = []
}

const deleteComment = async (commentId) => {
    const currentUserId = getCurrentUserId()
    const currentUserName = getCurrentUserName()
    const currentUserPicture = getCurrentUserPicture()

    const deleteData = {
        commentId: commentId,
        deleter: {
            id: currentUserId,
            name: currentUserName,
            picture: currentUserPicture
        }
    }

    try {
        commentLoading.value = true
        const response = await api.cardAPI.deleteComment(deleteData)
        
        if (response.status === 200) {
            // 更新本地狀態（軟刪除）
            const index = cardComments.value.findIndex(c => c.id === commentId)
            if (index !== -1) {
                cardComments.value[index].isDeleted = true
            }
            activeCommentMenu.value = null
            Message.success(t('htwbcard.collect.comments.deleted'))
        }
    } catch (error) {
        console.error('刪除留言失敗:', error)
        Message.error(t('htwbcard.collect.comments.deleteFailed'))
    } finally {
        commentLoading.value = false
    }
}

const formatCommentTime = (timestamp) => {
    const date = new Date(timestamp * 1000)
    const now = new Date()
    const diffInMinutes = Math.floor((now - date) / (1000 * 60))
    
    if (diffInMinutes < 1) return t('htwbcard.collect.comments.justNow')
    if (diffInMinutes < 60) return t('htwbcard.collect.comments.minutesAgo', { minutes: diffInMinutes })
    
    const diffInHours = Math.floor(diffInMinutes / 60)
    if (diffInHours < 24) return t('htwbcard.collect.comments.hoursAgo', { hours: diffInHours })
    
    const diffInDays = Math.floor(diffInHours / 24)
    if (diffInDays < 7) return t('htwbcard.collect.comments.daysAgo', { days: diffInDays })
    
    return date.toLocaleDateString()
}

// 編輯留言附件管理函數
const removeEditAttachment = (comment, index) => {
    if (comment.editAttachments && comment.editAttachments[index]) {
        comment.editAttachments.splice(index, 1)
    }
}

const handleEditCommentFileSelect = async (event, comment) => {
    const files = Array.from(event.target.files || [])
    if (files.length === 0) return

    // 確保 editAttachments 陣列存在
    if (!comment.editAttachments) {
        comment.editAttachments = []
    }

    // 限制只能有一個檔案，清空現有附件
    if (files.length > 0) {
        comment.editAttachments = []
    }

    try {
        commentLoading.value = true
        
        // 只處理第一個檔案
        const file = files[0]
        
        // 檔案大小檢查 (5MB)
        if (file.size > 5 * 1024 * 1024) {
            Message.error(t('htwbcard.collect.comments.fileTooLarge', { fileName: file.name }))
            return
        }

        // 建立檔案路徑：collectId/cardId/timestamp_filename （與原始格式一致）
        const timestamp = Date.now()
        const fileName = `${timestamp}_${file.name}`
        const filePath = `${cardCollectInfo.value.id}/${comment.cardId}/${fileName}`
        
        try {
            await uploadFileToBlob(filePath, file)
            // 新增附件到編輯列表（替換所有現有附件）
            comment.editAttachments = [{
                id: timestamp.toString(),
                filePath: filePath,
                uploadTime: Math.floor(Date.now() / 1000)
            }]
            
            Message.success(t('htwbcard.collect.comments.fileUploaded', { fileName: file.name }))
        } catch (uploadError) {
            console.error('檔案上傳失敗:', uploadError)
            Message.error(t('htwbcard.collect.comments.fileUploadFailed', { fileName: file.name }))
        }
    } catch (error) {
        console.error('處理檔案選擇失敗:', error)
        Message.error(t('htwbcard.collect.comments.fileProcessFailed'))
    } finally {
        commentLoading.value = false
        // 清空文件輸入
        event.target.value = ''
    }
}

const updateShareSettings = async () => {
    try {
        // 從 localStorage 獲取當前用戶資料
        const savedProfile = localStorage.getItem("profile")
        let currentUser = null
        
        if (savedProfile) {
            try {
                const profileData = JSON.parse(savedProfile)
                currentUser = {
                    id: profileData.id || profileData.teammodelId,
                    name: profileData.name
                }
            } catch (error) {
                console.error('解析用戶資料失敗:', error)
            }
        }
        
        if (!currentUser || !currentUser.id) {
            Message.error(t('htwbcard.collect.errors.missingUserInfo'))
            return
        }
        
        const requestData = {
            id: cardCollectInfo.value.id,
            cardCollectName: shareCardCollectName.value.trim(),
            openSubmit: shareOpenSubmit.value,
            editor: {
                id: currentUser.id,
                name: currentUser.name
            }
        }
        
        console.log('Update request data:', requestData)
        
        const response = await api.cardAPI.updateCardCollect(requestData)
        
        if (response) {
            showShareModal.value = false
            await getCardCollectData() // 重新載入資料
            Message.success(t('htwbcard.collect.successMessages.settingsUpdated'))
        }
    } catch (error) {
        console.error('更新分享設定失敗:', error)
        Message.error(t('htwbcard.collect.errors.updateFailed'))
    }
}

// 檔案選擇處理
const handleFileSelect = (event) => {
    const file = event.target.files[0]
    if (file) {
        validateAndSetFile(file)
    }
}

// 檔案拖拽相關處理（用於檔案上傳）
const handleFileDragOver = (event) => {
    event.preventDefault()
    event.dataTransfer.dropEffect = 'copy'
}

const handleFileDragEnter = (event) => {
    event.preventDefault()
    isDragOver.value = true
}

const handleFileDragLeave = (event) => {
    event.preventDefault()
    // 只有當離開整個拖拽區域時才設為 false
    if (!event.currentTarget.contains(event.relatedTarget)) {
        isDragOver.value = false
    }
}

const handleFileDrop = (event) => {
    event.preventDefault()
    isDragOver.value = false
    
    const files = event.dataTransfer.files
    if (files.length > 0) {
        validateAndSetFile(files[0])
    }
}

// 檔案驗證和設定
const validateAndSetFile = (file) => {
    // 清除之前的錯誤
    uploadForm.errors.file = ''
    uploadForm.generalError = ''

    // 檔案大小驗證
    const maxSizeBytes = maxFileSize.value * 1024 * 1024
    if (file.size > maxSizeBytes) {
        uploadForm.errors.file = t('htwbcard.collect.errors.fileSizeExceeded', { size: maxFileSize.value })
        return
    }

    // 移除檔案類型驗證，接受所有檔案
    // 任何檔案類型都可以上傳

    uploadForm.selectedFile = file
    
    // 清除內容相關的錯誤訊息，因為現在有檔案了
    if (uploadForm.errors.textContent) {
        delete uploadForm.errors.textContent
    }
    if (uploadForm.errors.file) {
        delete uploadForm.errors.file
    }
    uploadForm.generalError = ''
    
    // 如果沒有填寫卡片名稱，自動使用檔案名稱（去除副檔名）
    if (!uploadForm.cardName.trim()) {
        const nameWithoutExt = file.name.substring(0, file.name.lastIndexOf('.')) || file.name
        uploadForm.cardName = nameWithoutExt
    }
    
    console.log("Selected file:", file)
}

// 移除檔案
const removeFile = () => {
    // 釋放預覽 URL資源
    if (uploadForm.selectedFile && isImageFile(uploadForm.selectedFile)) {
        const previewUrl = getFilePreviewUrl(uploadForm.selectedFile)
        if (previewUrl) {
            URL.revokeObjectURL(previewUrl)
        }
    }
    
    uploadForm.selectedFile = null
    uploadForm.errors.file = ''
    if (fileInput.value) {
        fileInput.value.value = ''
    }
}

// 格式化檔案大小
const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes'
    const k = 1024
    const sizes = ['Bytes', 'KB', 'MB', 'GB']
    const i = Math.floor(Math.log(bytes) / Math.log(k))
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
}

// 獲取檔案類型（原始 MIME type）
const getFileType = (file) => {
    return file.type || 'application/octet-stream'
}

// 獲取顯示用的檔案類型
const getDisplayFileType = (mimeType) => {
    if (!mimeType) return t('htwbcard.collect.fileTypes.unknown')
    if (mimeType.startsWith('image/')) return t('htwbcard.collect.fileTypes.image')
    if (mimeType.startsWith('video/')) return t('htwbcard.collect.fileTypes.video')
    if (mimeType.startsWith('audio/')) return t('htwbcard.collect.fileTypes.audio')
    if (mimeType === 'application/pdf') return 'PDF'
    if (mimeType.includes('word')) return t('htwbcard.collect.fileTypes.word')
    if (mimeType.includes('powerpoint') || mimeType.includes('presentation')) return 'PowerPoint'
    if (mimeType === 'text/plain') return t('htwbcard.collect.fileTypes.text')
    if (mimeType === 'application/zip') return t('htwbcard.collect.fileTypes.zip')
    if (mimeType === 'application/x-rar-compressed') return t('htwbcard.collect.fileTypes.rar')
    if (mimeType === 'application/octet-stream') return t('htwbcard.collect.fileTypes.general')
    
    // 對於其他未知的 MIME type，嘗試從中提取有用資訊
    if (mimeType.includes('text/')) return t('htwbcard.collect.fileTypes.textFile')
    if (mimeType.includes('application/')) return t('htwbcard.collect.fileTypes.applicationFile')
    
    // 如果完全無法辨識，直接回傳原始 MIME type
    return mimeType
}

// 檢查是否為圖片檔案
const isImageFile = (file) => {
    return file && file.type.startsWith('image/')
}

// 檢查是否為影片檔案
const isVideoFile = (file) => {
    return file && file.type.startsWith('video/')
}

// 檢查是否為影片檔案類型（字串版本）
const isVideoFileType = (fileType) => {
    return fileType && fileType.startsWith('video/')
}

// 檢查是否為音訊檔案類型（字串版本）
const isAudioFileType = (fileType) => {
    return fileType && fileType.startsWith('audio/')
}

// 獲取檔案預覽 URL（用於上傳前預覽）
const getFilePreviewUrl = (file) => {
    if (!file || !isImageFile(file)) {
        return ''
    }
    
    try {
        return URL.createObjectURL(file)
    } catch (error) {
        console.error('生成檔案預覽 URL 失敗:', error)
        return ''
    }
}

// 預覽上傳的圖片
const previewUploadImage = () => {
    if (!uploadForm.selectedFile || !isImageFile(uploadForm.selectedFile)) {
        return
    }
    
    // 創建一個臨時卡片物件來顯示預覽
    const tempCard = {
        id: 'temp-upload-preview',
        cardName: uploadForm.selectedFile.name,
        fileType: getFileType(uploadForm.selectedFile),
        tempImageUrl: getFilePreviewUrl(uploadForm.selectedFile)
    }
    
    previewCard.value = tempCard
    showImagePreview.value = true
    document.body.style.overflow = 'hidden'
}

// 關閉上傳 Modal
const closeUploadModal = () => {
    if (uploading.value) return // 上傳中不允許關閉
    
    showUploadModal.value = false
    resetUploadForm()
}

// 重置上傳表單
const resetUploadForm = () => {
    uploadForm.cardName = ''
    uploadForm.selectedFile = null
    uploadForm.textContent = ''
    uploadForm.cardColor = '#FFFFFF' // 重置為預設顏色(白色)
    // 使用當前選擇的分組，如果沒有則使用第一個分組
    uploadForm.groupId = selectedGroupId.value || (cardCollectInfo.value?.groups?.[0]?.groupId) || 'group-1'
    uploadForm.errors = {}
    uploadForm.generalError = ''
    uploading.value = false
    uploadProgress.value = 0
    uploadStatus.value = t('htwbcard.collect.uploadStatus.preparing')
    isDragOver.value = false
    if (fileInput.value) {
        fileInput.value.value = ''
    }
}

// 關閉新增組別 Modal
const closeAddGroupModal = () => {
    showAddGroupModal.value = false
    newGroupForm.groupName = ''
    newGroupForm.errors = {}
    newGroupForm.generalError = ''
}

// 驗證新增分組表單
const validateNewGroupForm = () => {
    newGroupForm.errors = {}
    newGroupForm.generalError = ''
    let isValid = true

    if (!newGroupForm.groupName.trim()) {
        newGroupForm.errors.groupName = t('htwbcard.collect.errors.groupNameRequired')
        isValid = false
    } else if (newGroupForm.groupName.trim().length > 20) {
        newGroupForm.errors.groupName = t('htwbcard.collect.errors.groupNameTooLong')
        isValid = false
    } else if (cardCollectInfo.value && cardCollectInfo.value.groups) {
        // 檢查分組名稱是否重複
        const existingGroup = cardCollectInfo.value.groups.find(g => 
            g.groupName.trim() === newGroupForm.groupName.trim()
        )
        if (existingGroup) {
            newGroupForm.errors.groupName = t('htwbcard.collect.errors.groupNameExists')
            isValid = false
        }
    }

    return isValid
}

// 新增分組
const addNewGroup = async () => {
    if (!validateNewGroupForm()) {
        return
    }

    if (isAddingGroup.value) return

    isAddingGroup.value = true

    try {
        // 生成新的分組 ID
        const timestamp = Date.now()
        const newGroupId = `group-${timestamp}`
        
        // 建立新分組
        const newGroup = {
            groupId: newGroupId,
            groupName: newGroupForm.groupName.trim(),
            members: []
        }

        // 更新 CardCollect 的 groups 陣列
        if (!cardCollectInfo.value.groups) {
            cardCollectInfo.value.groups = []
        }
        cardCollectInfo.value.groups.push(newGroup)

        // 呼叫後端 API 更新 CardCollect
        const collectId = route.params.id || route.query.id
        const updateData = {
            id: collectId,
            groups: cardCollectInfo.value.groups,
            editor: {
                id: 'system',
                name: t('htwbcard.collect.userIdentity.systemUser')
            }
        }

        await api.cardAPI.updateCardCollect(updateData)
        
        // 切換到新建立的分組
        selectedGroupId.value = newGroupId
        
        // 關閉 Modal
        closeAddGroupModal()
        
        Message.success(t('htwbcard.collect.successMessages.groupAdded'))
        
    } catch (error) {
        console.error('新增分組失敗:', error)
        newGroupForm.generalError = '新增分組失敗，請稍後再試'
    } finally {
        isAddingGroup.value = false
    }
}

// 表單驗證
const validateForm = () => {
    uploadForm.errors = {}
    uploadForm.generalError = ''
    let isValid = true

    // 驗證卡片名稱
    if (!uploadForm.cardName.trim()) {
        uploadForm.errors.cardName = t('htwbcard.collect.errors.cardNameRequired')
        isValid = false
    } else if (uploadForm.cardName.trim().length > 50) {
        uploadForm.errors.cardName = t('htwbcard.collect.errors.cardNameTooLong')
        isValid = false
    }

    // 驗證內容：至少要有文字或檔案其中之一
    const hasText = uploadForm.textContent.trim()
    const hasFile = uploadForm.selectedFile
    
    if (!hasText && !hasFile) {
        uploadForm.errors.textContent = t('htwbcard.collect.errors.contentRequired')
        uploadForm.errors.file = t('htwbcard.collect.errors.contentRequired')
        isValid = false
    }

    return isValid
}

// 檢查是否為圖片檔案類型
const isImageFileType = (fileType) => {
    return fileType && fileType.startsWith('image/')
}

// 獲取卡片檔案 URL（通用版本，支援所有檔案類型）
const getCardFileUrl = (card) => {
    if (!card || !card.filePath) {
        return ''
    }
    
    const sasToken = sessionStorage.getItem('card_sas_token')
    if (!sasToken) {
        console.warn('SAS Token 不存在')
        return ''
    }
    
    try {
        const containerName = 'ht-sharing-card'
        const fileUrl = `${blobDomainUrl}/${containerName}/${card.filePath}?${sasToken}`
        
        return fileUrl
    } catch (error) {
        console.error('獲取檔案 URL 失敗:', error)
        return ''
    }
}

// 保持舊函數名稱以避免破壞現有功能
const getCardImageUrl = (card) => {
    // 如果是附件預覽，使用附件的路徑
    if (card.id && card.id.startsWith('attachment-')) {
        return getAttachmentUrl(card.filePath)
    }
    
    // 否則使用原本的卡片檔案 URL
    return getCardFileUrl(card)
}



// 圖片載入成功
const onImageLoad = (cardId) => {
    imageErrorMap.value[cardId] = false
    // console.log(`卡片 ${cardId} 的圖片載入成功`)
}

// 圖片載入失敗
const onImageError = (cardId) => {
    imageErrorMap.value[cardId] = true
    console.warn(`卡片 ${cardId} 的圖片載入失敗`)
}

// 影片載入失敗
const onVideoError = (cardId) => {
    console.warn(`卡片 ${cardId} 的影片載入失敗`)
}

// 音訊載入失敗
const onAudioError = (cardId) => {
    console.warn(`卡片 ${cardId} 的音訊載入失敗`)
}

// 獲取原始檔案名稱（從路徑中提取）
const getOriginalFileName = (card) => {
    if (!card || !card.filePath) {
        return card?.cardName || 'download'
    }
    
    // 從路徑中提取檔案名稱，格式通常是 collectId/cardId/timestamp_originalFileName
    const pathParts = card.filePath.split('/')
    if (pathParts.length > 0) {
        const fileName = pathParts[pathParts.length - 1]
        
        // 移除時間戳前綴（格式：timestamp_originalFileName）
        // 時間戳通常是 13 位數字後面跟著底線
        const timestampPattern = /^\d{13}_/
        let cleanFileName = fileName.replace(timestampPattern, '')
        
        // 如果第一個模式沒有匹配，嘗試更寬鬆的模式
        if (cleanFileName === fileName) {
            const timestampPattern2 = /^\d+_/
            cleanFileName = fileName.replace(timestampPattern2, '')
        }
        
        // 如果清理後的檔名不為空且不同於原檔名，則使用清理後的檔名
        if (cleanFileName && cleanFileName !== fileName) {
            return cleanFileName
        }
        
        // 如果無法清理或清理失敗，使用卡片名稱
        const cardName = card.cardName || 'download'
        
        // 嘗試從原始檔名中提取副檔名
        const fileExtension = fileName.includes('.') ? fileName.split('.').pop() : ''
        
        // 如果卡片名稱已經有副檔名，直接使用
        if (cardName.includes('.')) {
            return cardName
        }
        
        // 如果有副檔名且副檔名合理，加上副檔名
        if (fileExtension && fileExtension.length <= 10) {
            return `${cardName}.${fileExtension}`
        }
        
        return cardName
    }
    
    return card.cardName || 'download'
}

// 下載檔案
const downloadFile = async (card) => {
    if (!card || !card.filePath) {
        return
    }
    
    try {
        const fileUrl = getCardFileUrl(card)
        const fileName = getOriginalFileName(card)
        
        console.log('=== 下載調試信息 ===')
        console.log('原始檔案路徑:', card.filePath)
        console.log('處理後的檔名:', fileName)
        console.log('下載URL:', fileUrl)
        
        // 使用 fetch API 下載檔案，確保檔名正確
        const response = await fetch(fileUrl)
        if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`)
        }
        
        const blob = await response.blob()
        
        // 創建 Blob URL
        const blobUrl = URL.createObjectURL(blob)
        
        // 創建下載連結
        const link = document.createElement('a')
        link.href = blobUrl
        link.download = fileName
        
        // 觸發下載
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
        
        // 清理 Blob URL
        URL.revokeObjectURL(blobUrl)
        
        console.log('檔案下載完成:', fileName)
    } catch (error) {
        console.error('下載檔案失敗:', error)
        // 如果 fetch 失敗，回退到原來的方法
        const fileUrl = getCardFileUrl(card)
        const fileName = getOriginalFileName(card)
        const link = document.createElement('a')
        link.href = fileUrl
        link.download = fileName
        link.target = '_blank'
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
    }
}

// 開啟圖片預覽
const openImagePreview = (card) => {
    if (!card || !isImageFileType(card.fileType)) {
        return
    }
    previewCard.value = card
    showImagePreview.value = true
    // 禁止背景滾動
    document.body.style.overflow = 'hidden'
}

// 關閉圖片預覽
const closeImagePreview = () => {
    showImagePreview.value = false
    previewCard.value = null
    // 恢復背景滾動
    document.body.style.overflow = ''
}

// 卡片拖拽開始
const handleCardDragStart = (event, card) => {
    draggedCard.value = card
    event.dataTransfer.effectAllowed = 'move'
    event.dataTransfer.setData('text/plain', card.id)
    
    // 添加拖拽樣式
    event.target.style.opacity = '0.5'
}

// 卡片拖拽結束
const handleCardDragEnd = (event) => {
    // 恢復樣式
    event.target.style.opacity = '1'
    isDragOverGroup.value = null
}

// 處理卡片拖拽到分組上方
const handleCardDragEnter = (event) => {
    event.preventDefault()
    if (draggedCard.value) {
        const columnCards = event.currentTarget
        const column = columnCards.closest('.column')
        if (column) {
            const groupButton = column.querySelector('.group-title-btn')
            if (groupButton && cardCollectInfo.value && cardCollectInfo.value.groups) {
                const group = cardCollectInfo.value.groups.find(g => g.groupName === groupButton.textContent.trim())
                if (group) {
                    isDragOverGroup.value = group.groupId
                }
            }
        }
    }
}

const handleCardDragLeave = (event) => {
    event.preventDefault()
    // 只有當真正離開區域時才清除樣式
    if (!event.currentTarget.contains(event.relatedTarget)) {
        isDragOverGroup.value = null
    }
}

// 處理卡片拖拽懸停
const handleCardDragOver = (event) => {
    event.preventDefault()
    event.dataTransfer.dropEffect = 'move'
}

// 處理卡片放置
const handleCardDrop = async (event, targetGroupId) => {
    event.preventDefault()
    isDragOverGroup.value = null
    
    if (!draggedCard.value) {
        return
    }
    
    const sourceCard = draggedCard.value
    const sourceGroupId = sourceCard.groupId || (cardCollectInfo.value?.groups?.[0]?.groupId)
    
    // 如果拖拽到同一個分組，則不處理
    if (sourceGroupId === targetGroupId) {
        draggedCard.value = null
        return
    }
    
    try {
        // 更新卡片的分組
        await updateCardGroup(sourceCard.id, targetGroupId)
        
        // 更新本地資料
        if (cardList.value) {
            const cardIndex = cardList.value.findIndex(c => c.id === sourceCard.id)
            if (cardIndex !== -1) {
                cardList.value[cardIndex].groupId = targetGroupId
            }
        }
        
        Message.success(t('htwbcard.collect.successMessages.cardMoved'))
    } catch (error) {
        console.error('移動卡片失敗:', error)
        Message.error(t('htwbcard.collect.errors.generalError'))
    } finally {
        draggedCard.value = null
    }
}

// 更新卡片分組的 API 呼叫
const updateCardGroup = async (cardId, newGroupId) => {
    const collectId = route.params.id || route.query.id
    const updateData = {
        id: cardId,
        collectId: collectId,
        groupId: newGroupId,
        owner: currentUser.value || {
            id: 'anonymous-user',
            name: t('htwbcard.collect.userIdentity.anonymousUser')
        }
    }
    
    const response = await api.cardAPI.updateCard(updateData)
    return response
}

// 格式化時間
const formatTime = (timestamp) => {
    if (!timestamp) return ''
    // 如果是 Unix 時間戳（秒），需要轉換為毫秒
    const date = new Date(timestamp * 1000)
    return date.toLocaleDateString('zh-TW')
}

// 返回首頁
const goHome = () => {
    router.push('/htwb-card-home')
}


// 獲取當前用戶資訊
const getCurrentUser = () => {
    // 使用統一的用戶身份識別系統
    const userId = getCurrentUserId()
    const userName = getCurrentUserName()
    
    currentUser.value = {
        id: userId,
        name: userName
    }
    
    console.log('設定當前用戶:', currentUser.value)
}

// 卡片檔案上傳（使用統一函數，含全域進度更新）
const uploadFileToBlob = async (filePath, file) => {
    return await uploadToAzureBlob(file, filePath, {
        onProgress: (progress) => {
            if (progress.loadedBytes && file.size > 0) {
                // 計算上傳進度百分比 (15% 到 80% 之間)
                const percentage = Math.round((progress.loadedBytes / file.size) * 65) + 15
                uploadProgress.value = Math.min(percentage, 80)
                
                // 更新狀態訊息
                const uploadedMB = (progress.loadedBytes / (1024 * 1024)).toFixed(1)
                const totalMB = (file.size / (1024 * 1024)).toFixed(1)
                uploadStatus.value = t('htwbcard.collect.uploadStatus.uploading', { uploaded: uploadedMB, total: totalMB })
            }
        }
    })
}

// 上傳卡片
const uploadCard = async () => {
    // 驗證表單
    if (!validateForm()) {
        return
    }

    if (uploading.value) return

    uploading.value = true
    uploadProgress.value = 10
    uploadStatus.value = t('htwbcard.collect.uploadStatus.preparing')
    uploadForm.generalError = ''

    try {
        // 獲取當前用戶資訊
        getCurrentUser()

        // 先建立卡片記錄（不含 filePath）
        uploadStatus.value = t('htwbcard.collect.uploadStatus.creating')
        uploadProgress.value = 20
        
        const collectId = route.params.id || route.query.id
        const cardData = {
            cardName: uploadForm.cardName.trim(),
            fileType: uploadForm.selectedFile ? getFileType(uploadForm.selectedFile) : 'text/plain',
            filePath: '', // 檔案路徑，稍後更新
            textContent: uploadForm.textContent.trim(), // 文字內容（可為空）
            cardColor: uploadForm.cardColor, // 卡片顏色
            collectId: collectId,
            owner: currentUser.value
        }

        // 如果有選擇分組，加入 groupId
        if (uploadForm.groupId) {
            cardData.groupId = uploadForm.groupId
        }

        const createResponse = await api.cardAPI.createCard(cardData)
        console.log("Card created:", createResponse)
        
        // 從後端回應中獲取生成的 cardId
        const cardId = createResponse.id
        
        // 如果有檔案，進行檔案上傳流程
        if (uploadForm.selectedFile) {
            // 使用後端生成的 cardId 構建檔案路徑
            const originalFileName = uploadForm.selectedFile.name
            const timestamp = new Date().getTime()
            const fileName = `${timestamp}_${originalFileName}`
            const filePath = `${collectId}/${cardId}/${fileName}`

            uploadStatus.value = t('htwbcard.collect.uploadStatus.uploading', { uploaded: '0', total: formatFileSize(uploadForm.selectedFile.size) })
            uploadProgress.value = 30
            
            // 上傳檔案到 Azure Blob Storage
            await uploadFileToBlob(filePath, uploadForm.selectedFile)

            // 更新卡片記錄中的 filePath
            uploadStatus.value = t('htwbcard.collect.uploadStatus.updating')
            uploadProgress.value = 85
            
            const updateData = {
                id: cardId,
                collectId: collectId,
                owner: currentUser.value,
                filePath: filePath
            }
            
            await api.cardAPI.updateCard(updateData)
            console.log("Card filePath updated:", filePath)
        } else {
            // 純文字卡片，直接跳到完成
            uploadStatus.value = t('htwbcard.collect.uploadStatus.saving')
            uploadProgress.value = 85
        }

        uploadStatus.value = uploadForm.selectedFile ? '上傳完成！' : '文字卡片建立成功！'
        uploadProgress.value = 100

        // 成功後關閉 Modal 並重新載入資料
        setTimeout(() => {
            closeUploadModal()
        }, 1000)
        
        await getCardCollectData()
        
        // 顯示成功訊息
        const successMessage = uploadForm.selectedFile ? t('htwbcard.collect.successMessages.cardUploaded') : t('htwbcard.collect.successMessages.textCardCreated')
        Message.success(successMessage)
        

    } catch (error) {
        console.error("Error uploading card:", error)
        console.error("Error response:", error.response?.data)
        console.error("Upload form data:", uploadForm)
        uploadStatus.value = t('htwbcard.collect.uploadStatus.failed')
        
        // 設定錯誤訊息
        let errorMessage = t('htwbcard.collect.uploadStatus.failed')
        if (error.response && error.response.data) {
            // 後端返回的具體錯誤訊息
            errorMessage = error.response.data || error.message
        } else if (error.message) {
            if (error.message.includes('SAS Token')) {
                errorMessage = t('htwbcard.collect.errors.sasTokenExpired')
            } else if (error.message.includes('檔案上傳失敗')) {
                errorMessage = t('htwbcard.collect.errors.fileUploadFailed')
            } else {
                errorMessage = error.message
            }
        }
        
        uploadForm.generalError = errorMessage
        Message.error(errorMessage)
    } finally {
        uploading.value = false
    }
}

// 組件掛載
onMounted(async () => {
    try {
        // 獲取卡片 SAS token
        await getCardCollectSas()
        
        // 啟動 token 自動刷新（針對 Card SAS）
        startCardSasRefreshInterval()
        
        // 獲取卡片收集資料
        await getCardCollectData()
        
        // 檢查是否從創建頁面跳轉來的，如果是則自動打開分享modal
        if (route.query.autoShare === 'true') {
            // 延遲一下讓資料載入完成
            setTimeout(() => {
                openShareModal()
            }, 100)
        }
        
        // 延遲檢查登入狀態，確保 LoginNavbar 組件已初始化
        setTimeout(() => {
            const savedProfile = localStorage.getItem("profile")
            const savedToken = localStorage.getItem("access_token")
            if (savedProfile && savedToken) {
                userLoggedIn.value = true
                console.log('Initial login state check, hasLoginUser:', hasLoginUser.value)
            }
        }, 100)
    } catch (error) {
        console.error("Error initializing card collect:", error)
        loading.value = false
    }
    
    // 點擊外部關閉選單
    document.addEventListener('click', (event) => {
        if (!event.target.closest('.card-menu') && !event.target.closest('.card-actions')) {
            showCardMenu.value = false
        }
        
        // 點擊外部關閉顏色篩選面板
        if (!event.target.closest('.color-filter-panel') && !event.target.closest('.color-filter-btn')) {
            showColorFilter.value = false
        }
        
        // 點擊外部關閉留言選單
        if (!event.target.closest('.comment-menu') && !event.target.closest('.comment-action-btn')) {
            activeCommentMenu.value = null
        }
    })
    
    // ESC 鍵關閉圖片預覽
    document.addEventListener('keydown', (event) => {
        if (event.key === 'Escape' && showImagePreview.value) {
            closeImagePreview()
        }
    })
})

// 清理
onBeforeUnmount(() => {
    // 清理定時器
    if (tokenRefreshInterval.value) {
        clearInterval(tokenRefreshInterval.value)
    }
})
</script>

<style lang="less">
@import "../assets/HTwbCardCSS/HTwbCardCollect.less";
</style>